<template>
  <div class="broadcast-layout">
    <Sidebar />

    <div class="main-content">
      <TopHeader />

      <main class="page-content">

        <!-- WhatsApp Access Gate -->
        <div v-if="noCredentials" class="wa-gate">
          <div class="wa-gate-icon">
            <svg width="56" height="56" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 1C6.48 1 2 5.48 2 11c0 2.39.86 4.58 2.26 6.31L3 21l3.84-1.22A9.95 9.95 0 0012 21c5.52 0 10-4.48 10-10S17.52 1 12 1zm0 18c-1.82 0-3.53-.5-4.99-1.38l-.35-.21-2.28.73.71-2.24-.23-.36A7.96 7.96 0 014 11c0-4.41 3.59-8 8-8s8 3.59 8 8-3.59 8-8 8zm4.5-5.5c-.24-.12-1.44-.71-1.66-.79-.22-.08-.39-.12-.55.12-.17.24-.63.79-.78.95-.14.16-.28.18-.52.06-.24-.12-1.01-.37-1.93-1.18-.71-.63-1.19-1.41-1.33-1.65-.14-.24-.01-.37.1-.49.11-.11.24-.28.36-.42.12-.14.16-.24.24-.4.08-.16.04-.3-.02-.42-.06-.12-.55-1.32-.75-1.81-.2-.47-.4-.41-.55-.42-.14-.01-.3-.01-.46-.01s-.42.06-.64.3c-.22.24-.84.82-.84 2.01 0 1.18.86 2.32 .98 2.49.12.16 1.69 2.58 4.1 3.62.57.25 1.02.4 1.37.51.58.18 1.1.15 1.51.09.46-.07 1.42-.58 1.62-1.14.2-.56.2-1.04.14-1.14-.06-.1-.23-.16-.47-.28z"/>
            </svg>
          </div>
          <h2 class="wa-gate-title">WhatsApp Not Connected</h2>
          <p class="wa-gate-message">You need to configure your WhatsApp account before you can create broadcast campaigns.</p>
          <router-link to="/whatsapp-settings" class="wa-gate-btn wa-gate-btn--primary">Go to WhatsApp Settings</router-link>
        </div>

        <div v-else-if="notVerified" class="wa-gate">
          <div class="wa-gate-icon wa-gate-icon--warning">
            <svg width="56" height="56" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
            </svg>
          </div>
          <h2 class="wa-gate-title">Business Portfolio Not Verified</h2>
          <p class="wa-gate-message">Your Meta Business Portfolio must be verified before you can create broadcast campaigns.</p>
          <a href="https://quali-d.com/tutorials/meta-verification-guide" target="_blank" rel="noopener" class="wa-gate-btn wa-gate-btn--primary">View Verification Guide</a>
        </div>

        <div v-else class="broadcast-container">
          <!-- Page Header -->
          <div class="page-header">
        <div class="header-content">
          <h1>Broadcast Campaigns</h1>
          <p class="subtitle">Create and manage WhatsApp broadcast campaigns using your approved templates</p>
        </div>
        <button @click="openCreateModal" class="create-campaign-btn">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
          </svg>
          Create Campaign
        </button>
      </div>

      <!-- Stats Overview -->
      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-icon" style="background: rgba(37, 211, 102, 0.1);">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="#25D366">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ stats.total_sent.toLocaleString() }}</div>
            <div class="stat-label">Total Sent</div>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-icon" style="background: rgba(59, 130, 246, 0.1);">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="#3B82F6">
              <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/>
            </svg>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ stats.delivery_rate }}%</div>
            <div class="stat-label">Delivery Rate</div>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-icon" style="background: rgba(168, 85, 247, 0.1);">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="#A855F7">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ stats.active_campaigns }}</div>
            <div class="stat-label">Active Campaigns</div>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-icon" style="background: rgba(251, 146, 60, 0.1);">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="#FB923C">
              <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
            </svg>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ stats.total_recipients.toLocaleString() }}</div>
            <div class="stat-label">Total Recipients</div>
          </div>
        </div>
      </div>

      <!-- Daily Messaging Limit Card -->
      <div v-if="whatsappStatus && whatsappStatus.has_credentials" class="daily-limit-card">
        <div class="daily-limit-icon">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
        </div>
        <div class="daily-limit-body">
          <div class="daily-limit-label">Daily WhatsApp Messages Limit</div>
          <div class="daily-limit-value">{{ formatDailyLimit(whatsappStatus.messaging_limit) }}</div>
        </div>
      </div>

      <!-- WhatsApp Budget Card -->
      <div class="wa-budget-card">

        <!-- Header -->
        <div class="wa-budget-header">
          <div class="wa-budget-title">
            <div class="wa-budget-icon-badge">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                <path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z"/>
              </svg>
            </div>
            <span>WhatsApp Budget</span>
          </div>
          <div class="wa-budget-header-right">
            <span v-if="budget && budget.billing_market" class="wa-budget-market-chip">{{ budget.billing_market }}</span>
            <button @click="openBudgetModal" class="budget-edit-btn">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>
              {{ (budget && budget.monthly_budget) ? 'Edit' : 'Set Budget' }}
            </button>
          </div>
        </div>

        <!-- Loading -->
        <div v-if="budgetLoading" class="wa-budget-loading">
          <div class="loading-spinner-small"></div>
          <span>Loading budget...</span>
        </div>

        <!-- Budget set -->
        <div v-else-if="budget && budget.monthly_budget" class="wa-budget-body">

          <!-- Month-end nudge banner -->
          <div v-if="showMonthEndNudge" class="budget-nudge-banner">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/></svg>
            <span><strong>{{ daysRemaining }} day{{ daysRemaining === 1 ? '' : 's' }} left</strong> — {{ formatUSD(budgetRemaining) }} unused. Consider scheduling more campaigns.</span>
          </div>

          <!-- Hero: big number + ring progress -->
          <div class="wa-budget-hero">
            <div class="wa-budget-hero-left">
              <div class="wa-budget-hero-label">Monthly Budget</div>
              <div class="wa-budget-hero-amount">{{ formatUSD(budget.monthly_budget) }}</div>
              <div class="wa-budget-hero-sub">
                <span :class="['wa-budget-hero-pct', budgetUsagePercent > 90 ? 'wa-budget-hero-pct--danger' : budgetUsagePercent > 70 ? 'wa-budget-hero-pct--warn' : 'wa-budget-hero-pct--good']">{{ budgetUsagePercent }}% used</span>
                <span class="wa-budget-hero-sep">·</span>
                <span :style="{ color: budgetRemaining < budget.monthly_budget * 0.1 ? '#ef4444' : 'var(--text-secondary)' }">{{ formatUSD(budgetRemaining) }} left</span>
              </div>
            </div>
            <div class="wa-budget-ring-wrap">
              <svg width="80" height="80" viewBox="0 0 80 80">
                <circle cx="40" cy="40" r="32" fill="none" stroke="var(--bg-secondary)" stroke-width="7"/>
                <circle
                  cx="40" cy="40" r="32"
                  fill="none"
                  :stroke="budgetUsagePercent > 90 ? '#ef4444' : budgetUsagePercent > 70 ? '#f59e0b' : '#25D366'"
                  stroke-width="7"
                  stroke-linecap="round"
                  stroke-dasharray="201.06"
                  :stroke-dashoffset="201.06 * (1 - Math.min(budgetUsagePercent, 100) / 100)"
                  transform="rotate(-90 40 40)"
                  style="transition: stroke-dashoffset 0.6s ease, stroke 0.3s;"
                />
              </svg>
              <div class="wa-budget-ring-center">
                <span class="wa-budget-ring-pct">{{ budgetUsagePercent }}%</span>
              </div>
            </div>
          </div>

          <!-- Metric tiles: Spent | Remaining | Est. Reach -->
          <div class="wa-budget-tiles">
            <div class="wa-budget-tile">
              <div class="wa-budget-tile-top">
                <span class="wa-budget-tile-label">Spent</span>
                <span v-if="spendDiffPercent !== null" :class="['wa-budget-diff', spendDiffPercent > 0 ? 'wa-budget-diff--up' : 'wa-budget-diff--down']">
                  {{ spendDiffPercent > 0 ? '↑' : '↓' }} {{ Math.abs(spendDiffPercent) }}%
                </span>
              </div>
              <div class="wa-budget-tile-value">{{ formatUSD(budget.spent_this_month || 0) }}</div>
              <div class="wa-budget-tile-sub">
                this month<span v-if="lastMonthUtilizationPercent !== null"> · last {{ lastMonthUtilizationPercent }}% used</span>
              </div>
            </div>
            <div class="wa-budget-tile-sep"></div>
            <div class="wa-budget-tile">
              <div class="wa-budget-tile-top">
                <span class="wa-budget-tile-label">Remaining</span>
              </div>
              <div class="wa-budget-tile-value" :style="{ color: budgetRemaining < budget.monthly_budget * 0.1 ? '#ef4444' : '#10b981' }">
                {{ formatUSD(budgetRemaining) }}
              </div>
              <div v-if="projectedMonthEndSpend !== null" class="wa-budget-tile-sub">
                proj. end: {{ formatUSD(projectedMonthEndSpend) }}
              </div>
            </div>
            <template v-if="remainingReach !== null">
              <div class="wa-budget-tile-sep"></div>
              <div class="wa-budget-tile">
                <div class="wa-budget-tile-top">
                  <span class="wa-budget-tile-label">Est. Reach</span>
                </div>
                <div class="wa-budget-tile-value">~{{ remainingReach.toLocaleString() }}</div>
                <div class="wa-budget-tile-sub">recipients reachable</div>
              </div>
            </template>
          </div>

          <!-- Spend breakdown: Marketing vs Utility -->
          <div v-if="totalCategorizedSpend > 0" class="wa-budget-breakdown">
            <div class="wa-budget-breakdown-title">Spend by type</div>
            <div class="wa-budget-breakdown-row">
              <span class="wa-budget-breakdown-label wa-budget-breakdown-label--mkt">Mkt</span>
              <div class="wa-budget-breakdown-track">
                <div class="wa-budget-breakdown-bar wa-budget-breakdown-bar--mkt" :style="{ width: marketingSpendPercent + '%' }"></div>
              </div>
              <span class="wa-budget-breakdown-val">{{ formatUSD(marketingSpend) }}</span>
              <span class="wa-budget-breakdown-pct">{{ marketingSpendPercent }}%</span>
            </div>
            <div class="wa-budget-breakdown-row">
              <span class="wa-budget-breakdown-label wa-budget-breakdown-label--util">Util</span>
              <div class="wa-budget-breakdown-track">
                <div class="wa-budget-breakdown-bar wa-budget-breakdown-bar--util" :style="{ width: utilitySpendPercent + '%' }"></div>
              </div>
              <span class="wa-budget-breakdown-val">{{ formatUSD(utilitySpend) }}</span>
              <span class="wa-budget-breakdown-pct">{{ utilitySpendPercent }}%</span>
            </div>
          </div>

          <!-- 3-month history bar chart -->
          <div v-if="budget.monthly_history && budget.monthly_history.length" class="wa-budget-history">
            <div class="wa-budget-history-title">3-Month Spend</div>
            <div class="wa-budget-history-bars">
              <div v-for="m in budget.monthly_history" :key="m.label" class="wa-budget-history-col">
                <div class="wa-budget-history-bar-wrap">
                  <div
                    class="wa-budget-history-bar"
                    :class="{ 'wa-budget-history-bar--current': m.is_current }"
                    :style="{ height: historyMax > 0 ? Math.max(6, (m.spent / historyMax) * 100) + '%' : '6%' }"
                  ></div>
                </div>
                <div class="wa-budget-history-amt">{{ formatUSD(m.spent) }}</div>
                <div class="wa-budget-history-lbl">{{ m.label }}</div>
              </div>
            </div>
          </div>

        </div>

        <!-- Empty state -->
        <div v-else class="wa-budget-empty">
          <div class="wa-budget-empty-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="10"/><path d="M12 8v4m0 4h.01"/></svg>
          </div>
          <div class="wa-budget-empty-copy">
            <strong>No budget set yet</strong>
            <span>Set a monthly limit to track spend, estimate campaign costs, and see projections.</span>
          </div>
          <button @click="openBudgetModal" class="wa-budget-empty-btn">Set Budget →</button>
        </div>

      </div>

      <!-- Campaigns Tabs -->
      <div class="campaigns-section">
        <div class="tabs-header">
          <button
            v-for="tab in tabs"
            :key="tab.value"
            @click="activeTab = tab.value"
            :class="['tab-btn', { active: activeTab === tab.value }]"
          >
            {{ tab.label }}
            <span v-if="tab.count > 0" class="tab-count">{{ tab.count }}</span>
          </button>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="loading-state">
          <div class="loading-spinner"></div>
          <p>Loading campaigns...</p>
        </div>

        <!-- Empty State -->
        <div v-else-if="filteredCampaigns.length === 0" class="empty-state">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <h3>No {{ activeTab }} campaigns</h3>
          <p>{{ getEmptyStateMessage() }}</p>
        </div>

        <!-- Campaigns Table -->
        <div v-else class="campaigns-table-wrapper">
          <table class="campaigns-table">
            <thead>
              <tr>
                <th class="col-campaign">Campaign</th>
                <th class="col-template">Template</th>
                <th class="col-num">Recipients</th>
                <th class="col-num">Sent</th>
                <th class="col-num">Delivered</th>
                <th class="col-num">Read</th>
                <th class="col-num">Failed</th>
                <th>Status</th>
                <th>Created By</th>
                <th>Created</th>
                <th class="col-num" title="Estimated total cost in USD">Cost</th>
                <th class="col-num" title="Cost per delivered message">Cost/Delivered</th>
                <th class="col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="campaign in filteredCampaigns" :key="campaign.id" class="campaign-row">
                <td class="campaign-name-cell">{{ campaign.campaign_name }}</td>
                <td>
                  <div class="template-cell">
                    <span>{{ campaign.template_name }}</span>
                    <span class="lang-chip">{{ campaign.template_language === 'ar' ? 'AR' : 'EN' }}</span>
                    <span v-if="campaign.template_category" :class="['template-cat-chip', campaign.template_category === 'MARKETING' ? 'template-cat-chip--mkt' : 'template-cat-chip--util']">
                      {{ campaign.template_category === 'MARKETING' ? 'Mkt' : 'Util' }}
                    </span>
                  </div>
                </td>
                <td class="num-cell">{{ campaign.total_recipients.toLocaleString() }}</td>
                <td class="num-cell">{{ campaign.sent_count }}</td>
                <td class="num-cell">{{ campaign.delivered_count }}</td>
                <td class="num-cell read-cell">{{ campaign.read_count || 0 }}</td>
                <td class="num-cell failed-cell">{{ campaign.failed_count }}</td>
                <td>
                  <span :class="['status-badge', campaign.status]">
                    {{ campaign.status.replace('_', ' ') }}
                  </span>
                  <div v-if="campaign.status === 'in_progress'" class="inline-progress">
                    <div class="inline-progress-bar">
                      <div class="inline-progress-fill" :style="{ width: campaign.progress_percentage + '%' }"></div>
                    </div>
                    <span class="inline-progress-pct">{{ campaign.progress_percentage }}%</span>
                  </div>
                </td>
                <td class="creator-cell">
                  <span v-if="campaign.created_by_name" class="creator-badge creator-badge--agent">
                    {{ campaign.created_by_name }}
                  </span>
                  <span v-else class="creator-badge creator-badge--owner">Owner</span>
                </td>
                <td class="date-cell">{{ formatDate(campaign.created_at) }}</td>
                <td class="num-cell cost-cell">
                  <span v-if="campaign.campaign_cost" class="cost-value">{{ formatUSD(campaign.campaign_cost) }}</span>
                  <span v-else class="cost-na">—</span>
                </td>
                <td class="num-cell cost-cell">
                  <span v-if="campaign.campaign_cost && campaign.delivered_count > 0" class="cost-value cost-per-delivered">
                    ${{ (campaign.campaign_cost / campaign.delivered_count).toFixed(4) }}
                  </span>
                  <span v-else class="cost-na">—</span>
                </td>
                <td>
                  <div class="action-btns">
                    <button @click="viewCampaignDetails(campaign)" class="action-btn" title="View Recipients">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
        </div>
      </main>
    </div>

    <!-- Create Campaign Modal -->
    <div v-if="showCreateModal" class="modal-overlay" @click="closeCreateModal">
      <div class="modal-container create-campaign-modal" @click.stop>
        <div class="modal-header">
          <h3>Create Broadcast Campaign</h3>
          <button @click="closeCreateModal" class="modal-close-btn">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>

        <div class="modal-content">
          <!-- Two-Column Layout: Left=Form Fields, Right=Live Preview -->
          <div class="modal-two-column-layout">
            <!-- LEFT COLUMN: All Form Fields -->
            <div class="left-form-column">
              <form @submit.prevent="handleFormSubmit" class="campaign-form">
                <!-- Campaign Name -->
                <div class="form-group">
                  <label for="campaign-name">Campaign Name <span class="required">*</span></label>
                  <input
                    id="campaign-name"
                    v-model="campaignForm.campaign_name"
                    type="text"
                    placeholder="e.g., Summer Sale 2024"
                    class="form-input"
                    required
                  />
                </div>

                <!-- Template Selection -->
                <div class="form-group">
                  <label for="template">WhatsApp Template <span class="required">*</span></label>
                  <select
                    id="template"
                    v-model="campaignForm.template_name"
                    class="form-select"
                    required
                    @change="loadTemplateDetails"
                  >
                    <option value="">Select template</option>
                    <option v-for="template in approvedTemplates" :key="template.name" :value="template.name">
                      {{ template.name }}
                    </option>
                  </select>
                </div>

                <!-- Language Info (Read-only) -->
                <div v-if="campaignForm.template_language" class="form-group">
                  <label>Template Language</label>
                  <div class="info-field">
                    <span>{{ campaignForm.template_language === 'ar' ? 'Arabic (ar)' : campaignForm.template_language === 'en_US' ? 'English (US)' : campaignForm.template_language }}</span>
                  </div>
                </div>

                <!-- Media picker (for templates with IMAGE / VIDEO / DOCUMENT headers) -->
                <div v-if="selectedTemplate && hasMediaHeader()" class="form-group">
                  <label>{{ getMediaHeaderType() }} <span class="required">*</span></label>

                  <!-- Mode tabs -->
                  <div class="media-picker-tabs">
                    <button type="button" :class="['media-tab', { 'media-tab--active': mediaMode === 'upload' }]" @click="setBroadcastMediaMode('upload')">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16h6v-6h4l-7-7-7 7h4v6zm-4 2h14v2H5v-2z"/></svg>
                      Upload
                    </button>
                    <button type="button" :class="['media-tab', { 'media-tab--active': mediaMode === 'existing' }]" @click="setBroadcastMediaMode('existing')">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M20 6h-8l-2-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 12H4V8h16v10z"/></svg>
                      My Media
                    </button>
                    <button type="button" :class="['media-tab', { 'media-tab--active': mediaMode === 'url' }]" @click="setBroadcastMediaMode('url')">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/></svg>
                      Direct URL
                    </button>
                  </div>

                  <!-- Upload New -->
                  <div v-if="mediaMode === 'upload'" class="media-picker-panel">
                    <input type="file" ref="broadcastMediaFile" class="media-file-hidden" :accept="broadcastMediaAcceptTypes" @change="onBroadcastMediaSelected" />
                    <div v-if="!mediaAssetUuid && !mediaUploadLoading" class="media-upload-area" @click="$refs.broadcastMediaFile.click()">
                      <svg width="30" height="30" viewBox="0 0 24 24" fill="currentColor" class="upload-icon"><path d="M9 16h6v-6h4l-7-7-7 7h4v6zm-4 2h14v2H5v-2z"/></svg>
                      <p class="upload-area-text">Click to select a {{ getMediaHeaderType().toLowerCase() }}</p>
                      <p class="upload-area-hint" v-if="getMediaHeaderType() === 'Video'">MP4 only &bull; Max 16 MB</p>
                      <p class="upload-area-hint" v-else-if="getMediaHeaderType() === 'Image'">JPG or PNG &bull; Max 5 MB</p>
                      <p class="upload-area-hint" v-else>PDF, DOC, DOCX &bull; Max 10 MB</p>
                    </div>
                    <div v-if="mediaUploadLoading" class="media-uploading">
                      <div class="loading-spinner-small"></div>
                      <span>Uploading...</span>
                    </div>
                    <div v-if="mediaAssetUuid && !mediaUploadLoading" class="media-asset-confirmed">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" style="color:#10b981;flex-shrink:0"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                      <span class="media-asset-name">{{ mediaAssetName }}</span>
                      <button type="button" class="media-clear-btn" @click="clearBroadcastMedia">Change</button>
                    </div>
                    <div v-if="mediaUploadError" class="media-upload-error">{{ mediaUploadError }}</div>
                  </div>

                  <!-- My Media (existing) -->
                  <div v-if="mediaMode === 'existing'" class="media-picker-panel">
                    <div v-if="mediaAssetsLoading" class="media-loading-state">
                      <div class="loading-spinner-small"></div>
                      <span>Loading saved media...</span>
                    </div>
                    <div v-else-if="filteredBroadcastMediaAssets.length === 0" class="media-empty-state">
                      No saved {{ getMediaHeaderType().toLowerCase() }} files yet. Upload one first.
                    </div>
                    <div v-else class="media-assets-list">
                      <div
                        v-for="asset in filteredBroadcastMediaAssets"
                        :key="asset.uuid"
                        :class="['media-asset-card', { 'media-asset-card--selected': mediaAssetUuid === asset.uuid }]"
                        @click="selectBroadcastMediaAsset(asset)"
                      >
                        <img v-if="asset.media_type === 'image' && asset.preview_url" :src="asset.preview_url" class="asset-preview-img" alt="" @contextmenu.prevent />
                        <video v-else-if="asset.media_type === 'video' && asset.preview_url" :src="asset.preview_url" class="asset-preview-img" preload="metadata" muted @contextmenu.prevent />
                        <div v-else class="asset-preview-fallback">
                          <svg v-if="asset.media_type === 'video'" width="28" height="28" viewBox="0 0 24 24" fill="currentColor"><path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"/></svg>
                          <svg v-else-if="asset.media_type === 'image'" width="28" height="28" viewBox="0 0 24 24" fill="currentColor"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg>
                          <svg v-else width="28" height="28" viewBox="0 0 24 24" fill="currentColor"><path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/></svg>
                          <span class="asset-fallback-name">{{ asset.original_filename }}</span>
                          <span class="asset-fallback-size">{{ formatFileSize(asset.file_size) }}</span>
                        </div>
                        <div v-if="(asset.media_type === 'image' || asset.media_type === 'video') && asset.preview_url" class="asset-name-overlay">{{ asset.original_filename }}</div>
                        <div v-if="mediaAssetUuid === asset.uuid" class="asset-selected-badge">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Direct URL (fallback) -->
                  <div v-if="mediaMode === 'url'" class="media-picker-panel">
                    <div class="url-input-wrap" :class="{ 'url-input--valid': broadcastUrlValid === true, 'url-input--invalid': broadcastUrlValid === false }">
                      <svg class="url-prefix-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/></svg>
                      <input
                        v-model="campaignForm[getMediaUrlField()]"
                        type="url"
                        class="url-text-input"
                        :placeholder="`https://example.com/${getMediaHeaderType().toLowerCase()}.${getMediaHeaderType() === 'Image' ? 'jpg' : getMediaHeaderType() === 'Video' ? 'mp4' : 'pdf'}`"
                      />
                      <svg v-if="broadcastUrlValid === true" class="url-status-icon url-valid-icon" width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                      <svg v-else-if="broadcastUrlValid === false" class="url-status-icon url-invalid-icon" width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
                    </div>
                    <div class="url-ext-row">
                      <span class="url-ext-label">Allowed:</span>
                      <span v-for="ext in broadcastUrlExtensions" :key="ext" class="url-ext-chip" :class="{ 'url-ext-chip--match': broadcastUrlValid === true && (campaignForm[getMediaUrlField()] || '').toLowerCase().endsWith('.' + ext) }">.{{ ext }}</span>
                    </div>
                    <p v-if="broadcastUrlValid === false" class="url-error-msg">URL must end with {{ broadcastUrlExtensions.map(e => '.' + e).join(', ') }}</p>
                  </div>
                </div>

                <!-- Target Audience -->
                <div v-if="selectedTemplate" class="form-group">
                  <label>Target Audience <span class="required">*</span></label>
                  <div class="radio-group">
                    <label class="radio-label">
                      <input type="radio" v-model="campaignForm.target_type" value="all_customers" />
                      <span>All Customers</span>
                    </label>
                    <label class="radio-label">
                      <input type="radio" v-model="campaignForm.target_type" value="by_category" />
                      <span>By Category</span>
                    </label>
                    <label class="radio-label">
                      <input type="radio" v-model="campaignForm.target_type" value="by_tag" />
                      <span>By Tag</span>
                    </label>
                    <label class="radio-label">
                      <input type="radio" v-model="campaignForm.target_type" value="custom_list" />
                      <span>Custom Phone Numbers</span>
                    </label>
                    <label class="radio-label">
                      <input type="radio" v-model="campaignForm.target_type" value="excel_list" />
                      <span>Excel/CSV Upload</span>
                    </label>
                  </div>
                </div>

                <!-- Category Selection (if by_category) -->
                <div v-if="campaignForm.target_type === 'by_category'" class="form-group">
                  <label>Select Categories</label>
                  <div v-if="categories.length === 0" class="field-hint" style="padding: 12px; background: rgba(239, 68, 68, 0.1); border-radius: 8px; color: #EF4444;">
                    No customer categories available. Please create categories in your customer management section first.
                  </div>
                  <div v-else class="checkbox-group">
                    <label v-for="category in categories" :key="category.id" class="checkbox-label">
                      <input
                        type="checkbox"
                        :value="category.id"
                        v-model="campaignForm.category_ids"
                      />
                      <span class="category-name-with-count">
                        <span class="category-name">{{ category.name }}</span>
                        <span class="category-count">{{ category.customer_count }} {{ category.customer_count === 1 ? 'customer' : 'customers' }}</span>
                      </span>
                    </label>
                  </div>
                </div>

                <!-- Tag Selection (if by_tag) -->
                <div v-if="campaignForm.target_type === 'by_tag'" class="form-group">
                  <label>Select Tags</label>
                  <div v-if="availableTags.length === 0" class="field-hint" style="padding: 12px; background: rgba(239, 68, 68, 0.1); border-radius: 8px; color: #EF4444;">
                    No tags available. Please create tags in your customer management section first.
                  </div>
                  <div v-else class="checkbox-group">
                    <label v-for="tag in availableTags" :key="tag.id" class="checkbox-label">
                      <input
                        type="checkbox"
                        :value="tag.id"
                        v-model="campaignForm.tag_ids"
                      />
                      <span class="tag-name-with-count">
                        <span class="tag-color-dot" :style="{ background: tag.color }"></span>
                        <span class="tag-name">{{ tag.name }}</span>
                        <span class="category-count">{{ tag.customer_count }} {{ tag.customer_count === 1 ? 'customer' : 'customers' }}</span>
                      </span>
                    </label>
                  </div>
                </div>

                <!-- Custom Phone Numbers (if custom_list) - Customer Search -->
                <div v-if="campaignForm.target_type === 'custom_list'" class="form-group">
                  <label>Select Customers</label>
                  <p class="field-hint">Search and add customers from your database</p>

                  <!-- Search Input -->
                  <div class="customer-search-container">
                    <div class="search-input-wrapper">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" class="search-icon">
                        <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                      </svg>
                      <input
                        type="text"
                        v-model="customerSearchQuery"
                        @input="searchCustomers"
                        placeholder="Search by name..."
                        class="customer-search-input"
                      />
                      <div v-if="searchingCustomers" class="search-loading-spinner"></div>
                    </div>

                    <!-- Search Results Dropdown -->
                    <div v-if="customerSearchResults.length > 0 && customerSearchQuery" class="customer-search-results">
                      <div
                        v-for="customer in customerSearchResults"
                        :key="customer.id"
                        @click="addCustomerToList(customer)"
                        class="customer-result-item"
                      >
                        <div class="customer-result-info">
                          <div class="customer-result-name">{{ customer.full_name }}</div>
                          <div class="customer-result-details">
                            <span class="customer-result-phone">{{ customer.phone_number }}</span>
                            <span v-if="customer.category_name" class="customer-result-category">{{ customer.category_name }}</span>
                          </div>
                        </div>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" class="add-customer-icon">
                          <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                        </svg>
                      </div>
                      <div v-if="customerSearchResults.length === 0 && !searchingCustomers" class="no-results">
                        No customers found
                      </div>
                    </div>
                  </div>

                  <!-- Selected Customers Pills -->
                  <div v-if="selectedCustomers.length > 0" class="selected-customers-container">
                    <div class="selected-customers-label">Selected Customers ({{ selectedCustomers.length }})</div>
                    <div class="selected-customers-pills">
                      <div
                        v-for="(customer, index) in selectedCustomers"
                        :key="customer.id || index"
                        class="customer-pill"
                      >
                        <div class="customer-pill-content">
                          <div class="customer-pill-name">{{ customer.full_name }}</div>
                          <div class="customer-pill-details">
                            <span class="customer-pill-phone">{{ customer.phone_number }}</span>
                            <span v-if="customer.category_name" class="customer-pill-category">• {{ customer.category_name }}</span>
                          </div>
                        </div>
                        <button type="button" @click="removeCustomerFromList(index)" class="customer-pill-remove">
                          ×
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Empty State -->
                  <div v-if="selectedCustomers.length === 0" class="empty-customer-list">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor" opacity="0.3">
                      <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
                    </svg>
                    <p>Search and select customers to add them to this campaign</p>
                  </div>
                </div>

                <!-- Excel Upload Section (if excel_list) -->
                <div v-if="campaignForm.target_type === 'excel_list'" class="form-group">
                  <label>Upload Excel/CSV File <span class="required">*</span></label>
                  <p class="field-hint">Upload a spreadsheet with customer data and phone numbers</p>

                  <div class="excel-upload-container">
                    <!-- File Upload Button -->
                    <input
                      type="file"
                      ref="excelFileInput"
                      @change="uploadExcelFile"
                      accept=".csv,.xlsx,.xls"
                      style="display: none"
                    />

                    <button
                      v-if="!excelFile"
                      type="button"
                      @click="$refs.excelFileInput.click()"
                      class="upload-excel-btn"
                      :disabled="uploadingExcel"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M9 16h6v-6h4l-7-7-7 7h4zm-4 2h14v2H5z"/>
                      </svg>
                      {{ uploadingExcel ? 'Uploading...' : 'Upload Excel/CSV File' }}
                    </button>

                    <!-- File Uploaded Indicator -->
                    <div v-if="excelFile" class="file-uploaded-indicator">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="#25D366">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                      </svg>
                      <span class="file-name">{{ excelFile.name }}</span>
                      <span class="file-rows">{{ excelTotalRows || (excelData ? excelData.length : 0) }} rows</span>
                      <button type="button" @click="clearExcelFile" class="clear-file-btn">×</button>
                    </div>
                  </div>
                </div>

                <!-- Phone Column Selector - HIGHLIGHTED & REQUIRED (Separate Section) -->
                <div v-if="campaignForm.target_type === 'excel_list' && excelColumns.length > 0" class="form-group">
                  <div class="excel-phone-column-highlight">
                    <div class="highlight-banner">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="#25D366">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                      </svg>
                      <span>Required: Select Phone Number Column</span>
                    </div>
                    <label>Phone Number Column <span class="required">*</span></label>
                    <p class="field-hint">Must be in international format — e.g. 201012345678, not 01012345678</p>
                    <select v-model="excelConfig.phone_column" class="excel-column-select" required>
                      <option value="">Select column...</option>
                      <option
                        v-for="col in excelColumns"
                        :key="col.letter"
                        :value="col.letter"
                      >
                        Column {{ col.letter }} - {{ col.label }}
                      </option>
                    </select>

                    <!-- Phone validation warning for selected column -->
                    <div
                      v-if="excelConfig.phone_column && excelPhoneWarnings && excelPhoneWarnings[excelConfig.phone_column] && excelPhoneWarnings[excelConfig.phone_column].invalid > 0"
                      class="excel-phone-warning"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="#e67e22">
                        <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
                      </svg>
                      <div>
                        <strong>{{ excelPhoneWarnings[excelConfig.phone_column].invalid }} number(s) will be skipped</strong> — missing country code or invalid format.
                        <span v-if="excelPhoneWarnings[excelConfig.phone_column].invalid_samples.length">
                          Examples: {{ excelPhoneWarnings[excelConfig.phone_column].invalid_samples.join(', ') }}
                        </span>
                        <br><small>Numbers must be in full international format (e.g. 201012345678, not 01012345678).</small>
                      </div>
                    </div>

                    <div v-if="excelData && excelData.length > 0" class="excel-summary">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                      </svg>
                      <span>{{ excelTotalRows || excelData.length }} recipients ready to send</span>
                    </div>
                  </div>
                </div>

                <!-- Name Column Selector - REQUIRED -->
                <div v-if="campaignForm.target_type === 'excel_list' && excelColumns.length > 0" class="form-group">
                  <div class="excel-phone-column-highlight">
                    <div class="highlight-banner">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="#25D366">
                        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                      </svg>
                      <span>Required: Select Customer Name Column</span>
                    </div>
                    <label>Customer Name Column <span class="required">*</span></label>
                    <p class="field-hint">Used to identify the contact — will be stored as the customer name</p>
                    <select v-model="excelConfig.name_column" class="excel-column-select" required>
                      <option value="">Select column...</option>
                      <option
                        v-for="col in excelColumns"
                        :key="col.letter"
                        :value="col.letter"
                      >
                        Column {{ col.letter }} - {{ col.label }}
                      </option>
                    </select>
                  </div>
                </div>

                <!-- Variable Mapping (ALWAYS VISIBLE when template selected) -->
                <div v-if="selectedTemplate && getAllTemplateVariables().length > 0" class="form-group">
                  <label>Variable Mapping</label>
                  <p class="field-hint">Map template variables to customer fields or static values</p>

                  <div class="variable-mappings-list">
                    <div
                      v-for="varIndex in getAllTemplateVariables()"
                      :key="varIndex"
                      class="variable-mapping-card"
                    >
                      <div class="mapping-header">
                        <div class="variable-badge">{{ formatVariable(varIndex) }}</div>

                        <!-- Three-way Toggle for Source Type -->
                        <div class="mapping-type-selector">
                          <label
                            class="type-option"
                            :class="{ active: getOrCreateMapping(varIndex).source_type === 'customer_field' }"
                          >
                            <input
                              type="radio"
                              :name="`source-type-${varIndex}`"
                              value="customer_field"
                              v-model="getOrCreateMapping(varIndex).source_type"
                            />
                            <span>Customer Field</span>
                          </label>

                          <label
                            class="type-option"
                            :class="{ active: getOrCreateMapping(varIndex).source_type === 'static_value' }"
                          >
                            <input
                              type="radio"
                              :name="`source-type-${varIndex}`"
                              value="static_value"
                              v-model="getOrCreateMapping(varIndex).source_type"
                            />
                            <span>Static Value</span>
                          </label>

                          <label
                            v-if="campaignForm.target_type === 'excel_list' && excelColumns.length > 0"
                            class="type-option"
                            :class="{ active: getOrCreateMapping(varIndex).source_type === 'excel_column' }"
                          >
                            <input
                              type="radio"
                              :name="`source-type-${varIndex}`"
                              value="excel_column"
                              v-model="getOrCreateMapping(varIndex).source_type"
                            />
                            <span>Excel Column</span>
                          </label>
                        </div>
                      </div>

                      <div class="mapping-input-container">
                        <!-- Customer Field Input -->
                        <template v-if="getOrCreateMapping(varIndex).source_type === 'customer_field'">
                          <select v-model="getOrCreateMapping(varIndex).customer_field" class="mapping-select">
                            <option value="">Select customer field...</option>
                            <option value="full_name">Customer Name</option>
                            <option value="phone_number">Phone Number</option>
                            <option value="email">Email</option>
                            <option value="category_name">Category Name</option>
                            <option value="category_id">Category ID</option>
                            <option value="city">City</option>
                            <option value="country">Country</option>
                            <option value="address">Address</option>
                            <option value="created_at">Registration Date</option>
                            <option value="id">Customer ID</option>
                          </select>
                          <input
                            v-model="getOrCreateMapping(varIndex).default_value"
                            type="text"
                            placeholder="Fallback if field is empty..."
                            class="mapping-input"
                          />
                        </template>

                        <!-- Static Value Input -->
                        <template v-if="getOrCreateMapping(varIndex).source_type === 'static_value'">
                          <input
                            v-model="getOrCreateMapping(varIndex).static_value"
                            type="text"
                            placeholder="Enter static text value..."
                            class="mapping-input full-width"
                          />
                        </template>

                        <!-- Excel Column Selector -->
                        <template v-if="getOrCreateMapping(varIndex).source_type === 'excel_column'">
                          <select v-model="getOrCreateMapping(varIndex).excel_column" class="mapping-select">
                            <option value="">Select Excel column...</option>
                            <option
                              v-for="col in excelColumns"
                              :key="col.letter"
                              :value="col.letter"
                            >
                              Column {{ col.letter }} - {{ col.label }}
                            </option>
                          </select>
                          <input
                            v-model="getOrCreateMapping(varIndex).default_value"
                            type="text"
                            placeholder="Fallback if cell is empty..."
                            class="mapping-input"
                          />
                        </template>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Auto-tag & categorise recipients (new feature) -->
                <div v-if="selectedTemplate" class="campaign-enrichment-panel">
                  <div class="enrichment-panel-header">
                    <span class="enrichment-badge">New</span>
                    <span class="enrichment-title">Auto-tag &amp; categorise recipients</span>
                    <span class="enrichment-subtitle">All recipients are automatically added to your customers list. Optionally assign a tag and/or category to them upon sending.</span>
                  </div>

                  <!-- Tags (multi-select) -->
                  <div class="form-group">
                    <label>Assign Tags</label>
                    <p v-if="availableTags.length === 0" class="field-hint enrichment-empty-hint">
                      No tags yet — create them in Customer Tags first.
                    </p>
                    <div v-else class="checkbox-group">
                      <label v-for="tag in availableTags" :key="tag.id" class="checkbox-label enrichment-checkbox">
                        <input type="checkbox" :value="tag.id" v-model="campaignForm.campaign_tag_ids" />
                        <span class="tag-checkbox-label">
                          <span class="tag-color-dot" :style="{ background: tag.color }"></span>
                          {{ tag.name }}
                        </span>
                      </label>
                    </div>
                  </div>

                  <!-- Category -->
                  <div class="form-group">
                    <label>Assign Category</label>
                    <select v-model="campaignForm.campaign_category_id" class="form-select enrichment-select">
                      <option :value="null">— No category —</option>
                      <option v-for="cat in categories" :key="cat.id" :value="cat.id">
                        {{ cat.name }}
                      </option>
                    </select>
                    <label v-if="campaignForm.campaign_category_id && (campaignForm.target_type === 'custom_list' || campaignForm.target_type === 'excel_list')" class="checkbox-label enrichment-checkbox">
                      <input type="checkbox" v-model="campaignForm.update_existing_category" />
                      <span>Also update category for existing customers</span>
                    </label>
                  </div>
                </div>

                <!-- Description -->
                <div class="form-group">
                  <label for="description">Description (Optional)</label>
                  <textarea
                    id="description"
                    v-model="campaignForm.description"
                    class="form-textarea"
                    placeholder="Brief description of this campaign"
                    rows="3"
                  ></textarea>
                </div>

                <!-- Form Actions -->
                <div class="form-actions">
                  <button type="button" @click="closeCreateModal" class="cancel-btn">
                    Cancel
                  </button>
                  <button type="submit" class="submit-btn" :disabled="creating || !isFormValid">
                    <div v-if="creating" class="loading-spinner-small"></div>
                    <span v-else>Create & Send</span>
                  </button>
                </div>

                <!-- Validation Hints (shown when form is invalid) -->
                <div v-if="!isFormValid" class="form-validation-hints">
                  <div class="validation-hint-title">⚠️ Please complete the following:</div>
                  <ul class="validation-hints-list">
                    <li v-if="!campaignForm.campaign_name || campaignForm.campaign_name.trim() === ''">
                      Enter a campaign name
                    </li>
                    <li v-if="!selectedTemplate">
                      Select a WhatsApp template
                    </li>
                    <li v-if="!campaignForm.target_type">
                      Choose a target audience
                    </li>
                    <li v-if="campaignForm.target_type === 'by_category' && (!campaignForm.category_ids || campaignForm.category_ids.length === 0)">
                      Select at least one category
                    </li>
                    <li v-if="campaignForm.target_type === 'custom_list' && selectedCustomers.length === 0">
                      Select at least one customer
                    </li>
                    <li v-if="campaignForm.target_type === 'excel_list' && (!excelFile || !excelData || excelData.length === 0)">
                      Upload an Excel/CSV file
                    </li>
                    <li v-if="campaignForm.target_type === 'excel_list' && excelData && excelData.length > 0 && !excelConfig.phone_column">
                      Select the phone number column
                    </li>
                    <li v-if="campaignForm.target_type === 'excel_list' && excelData && excelData.length > 0 && !excelConfig.name_column">
                      Select the customer name column
                    </li>
                    <li v-if="hasMediaHeader() && mediaMode === 'url' && (!campaignForm[getMediaUrlField()] || campaignForm[getMediaUrlField()].trim() === '')">
                      Provide {{ getMediaHeaderType() }} URL
                    </li>
                    <li v-if="hasMediaHeader() && mediaMode !== 'url' && !mediaAssetUuid">
                      Select or upload a {{ getMediaHeaderType().toLowerCase() }} file
                    </li>
                    <li v-if="selectedTemplate && getAllTemplateVariables().length > 0 && !areAllVariablesMapped()">
                      Map all template variables
                    </li>
                  </ul>
                </div>
              </form>
            </div>

            <!-- RIGHT COLUMN: Live Preview (Always Visible, Sticky at Top) -->
            <div v-if="selectedTemplate" class="right-preview-column">
              <div class="sticky-preview-wrapper">
                <!-- Total Recipients Info -->
                <div v-if="totalRecipients > 0" class="recipients-info-card">
                  <div class="recipients-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
                    </svg>
                  </div>
                  <div class="recipients-content">
                    <div class="recipients-label">Total Messages to Send</div>
                    <div class="recipients-count">{{ totalRecipients.toLocaleString() }}</div>
                    <div class="recipients-hint">
                      <template v-if="campaignForm.target_type === 'all_customers'">
                        All customers in your database
                      </template>
                      <template v-else-if="campaignForm.target_type === 'by_category'">
                        Customers in selected categories
                      </template>
                      <template v-else-if="campaignForm.target_type === 'by_tag'">
                        Customers with selected tags
                      </template>
                      <template v-else-if="campaignForm.target_type === 'custom_list'">
                        Selected customers
                      </template>
                      <template v-else-if="campaignForm.target_type === 'excel_list'">
                        Excel/CSV upload
                      </template>
                    </div>
                  </div>
                </div>

                <div class="preview-phone">
                  <div class="phone-header">
                    <div class="phone-time">9:41</div>
                    <div class="phone-title">WhatsApp</div>
                  </div>
                  <div class="phone-chat">
                    <div class="chat-date">Today</div>
                    <div class="message-bubble">
                      <div v-if="getHeaderComponent()" class="message-header">
                        <div v-if="getHeaderComponent().format === 'TEXT'" class="header-text">
                          {{ replaceVariablesInText(getHeaderComponent().text) }}
                        </div>
                        <!-- Image Header -->
                        <div v-else-if="getHeaderComponent().format === 'IMAGE'" class="header-media">
                          <img
                            v-if="(campaignForm.image_url || previewMediaUrl) && !previewImageError"
                            :src="campaignForm.image_url || previewMediaUrl"
                            alt="Template image"
                            class="preview-image"
                            style="pointer-events: none;"
                            @error="handleImageError"
                            @contextmenu.prevent
                          />
                          <div v-else-if="previewImageError" class="media-placeholder media-error">
                            📷 Invalid image URL
                          </div>
                          <div v-else class="media-placeholder">
                            📷 Image
                          </div>
                        </div>
                        <!-- Video Header -->
                        <div v-else-if="getHeaderComponent().format === 'VIDEO'" class="header-media">
                          <video
                            v-if="(campaignForm.video_url || previewMediaUrl) && !previewVideoError"
                            :src="campaignForm.video_url || previewMediaUrl"
                            autoplay
                            muted
                            loop
                            playsinline
                            class="preview-video"
                            style="pointer-events: none;"
                            @error="handleVideoError"
                          >
                            Your browser does not support the video tag.
                          </video>
                          <div v-else-if="previewVideoError" class="media-placeholder media-error">
                            🎥 Invalid video URL
                          </div>
                          <div v-else class="media-placeholder">
                            🎥 Video
                          </div>
                        </div>
                        <!-- Document Header -->
                        <div v-else-if="getHeaderComponent().format === 'DOCUMENT'" class="header-media">
                          <a
                            v-if="campaignForm.document_url"
                            :href="campaignForm.document_url"
                            target="_blank"
                            class="preview-document"
                          >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
                            </svg>
                            <span>{{ getDocumentName() }}</span>
                          </a>
                          <div v-else class="media-placeholder">
                            📄 Document
                          </div>
                        </div>
                      </div>
                      <div class="message-body">
                        {{ replaceVariablesInText(getBodyComponent()?.text || '') }}
                      </div>
                      <div v-if="getFooterComponent()" class="message-footer">
                        {{ getFooterComponent().text }}
                      </div>
                      <div v-if="getButtonsComponent()" class="message-buttons">
                        <div v-for="(button, idx) in getButtonsComponent().buttons" :key="idx" class="wa-button">
                          <span class="button-icon">{{ button.type === 'URL' ? '🔗' : button.type === 'PHONE_NUMBER' ? '📞' : '⚡' }}</span>
                          {{ button.text }}
                        </div>
                      </div>
                      <div class="message-time">9:41 AM</div>
                    </div>
                  </div>
                  <div class="preview-info">
                    <div class="info-icon">💡</div>
                    <div class="info-text">
                      <template v-if="campaignForm.target_type === 'excel_list' && excelData && excelData.length > 0">
                        Showing first row from Excel ({{ excelTotalRows || excelData.length }} total recipients)
                      </template>
                      <template v-else-if="campaignForm.target_type === 'custom_list' && selectedCustomers.length > 0">
                        Previewing: {{ selectedCustomers[0].full_name }} ({{ selectedCustomers.length }} total recipients)
                      </template>
                      <template v-else>
                        Preview updates as you change variable mappings
                      </template>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Audience confirmation dialog — overlays the modal -->
        <transition name="confirm-fade">
          <div v-if="showAudienceWarning" class="confirm-dialog-overlay">
            <div :class="['confirm-dialog', { 'confirm-dialog--danger': campaignForm.target_type === 'all_customers' || totalRecipients > 100 }]">
              <div class="confirm-dialog-icon-row">
                <div class="confirm-dialog-icon-wrap">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor"><path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/></svg>
                </div>
              </div>
              <h4 class="confirm-dialog-title">
                <template v-if="campaignForm.target_type === 'all_customers' || totalRecipients > 100">
                  Large Broadcast — Are you sure?
                </template>
                <template v-else>
                  Confirm Broadcast
                </template>
              </h4>
              <p class="confirm-dialog-body">
                You are about to send this campaign to
                <strong>{{ totalRecipients.toLocaleString() }} {{ totalRecipients === 1 ? 'recipient' : 'recipients' }}</strong><template v-if="campaignForm.target_type === 'all_customers'">, which is your <strong>entire customer base</strong></template>.
              </p>
              <p v-if="campaignForm.target_type === 'all_customers' || totalRecipients > 100" class="confirm-dialog-caution">
                This action cannot be undone once started and will consume WhatsApp message credits accordingly.
              </p>

              <!-- Campaign Cost Estimate -->
              <div v-if="estimatedCampaignCost !== null" class="campaign-cost-estimate">
                <div class="cost-estimate-header">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z"/></svg>
                  Estimated Campaign Cost
                </div>
                <div class="cost-estimate-rows">
                  <div class="cost-estimate-row">
                    <span>Template type</span>
                    <span :class="['cost-type-badge', templatePricingType === 'marketing' ? 'cost-type-badge--marketing' : 'cost-type-badge--utility']">{{ templatePricingLabel }}</span>
                  </div>
                  <div v-if="budget && budget.billing_market" class="cost-estimate-row">
                    <span>Market</span>
                    <span>{{ budget.billing_market }}</span>
                  </div>
                  <div class="cost-estimate-row">
                    <span>Cost per message</span>
                    <span>${{ pricePerMessage.toFixed(4) }} USD</span>
                  </div>
                  <div class="cost-estimate-row cost-estimate-row--total">
                    <span>Total estimated cost</span>
                    <strong>{{ formatUSD(estimatedCampaignCost) }}</strong>
                  </div>
                  <template v-if="budget && budget.monthly_budget">
                    <div class="cost-estimate-divider"></div>
                    <div class="cost-estimate-row">
                      <span>Budget remaining now</span>
                      <span>{{ formatUSD(budgetRemaining) }}</span>
                    </div>
                    <div class="cost-estimate-row" :class="budgetAfterCampaign === 0 && estimatedCampaignCost > budgetRemaining ? 'cost-estimate-row--over' : ''">
                      <span>Budget remaining after</span>
                      <strong :style="{ color: estimatedCampaignCost > budgetRemaining ? '#ef4444' : '#10b981' }">
                        {{ estimatedCampaignCost > budgetRemaining ? '⚠ Over budget — ' : '' }}{{ formatUSD(budgetAfterCampaign) }}
                      </strong>
                    </div>
                  </template>
                </div>
              </div>
              <!-- Prompt to set budget if not set -->
              <div v-else-if="!budget || !budget.monthly_budget" class="cost-no-budget-hint">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" style="color:var(--text-tertiary);flex-shrink:0"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                <span>Set a budget and market to see cost estimates for your campaigns.</span>
              </div>

              <div class="confirm-dialog-actions">
                <button type="button" class="confirm-dialog-cancel" @click="showAudienceWarning = false">Go back</button>
                <button
                  type="button"
                  :class="['confirm-dialog-confirm', { 'confirm-dialog-confirm--blocked': campaignExceedsBudget }]"
                  :disabled="campaignExceedsBudget"
                  :title="campaignExceedsBudget ? 'Campaign cost exceeds your remaining monthly budget' : ''"
                  @click="showAudienceWarning = false; createCampaign()"
                >
                  <template v-if="campaignExceedsBudget">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/></svg>
                    Over budget — increase limit to send
                  </template>
                  <template v-else>
                    Send to {{ totalRecipients.toLocaleString() }} recipients
                  </template>
                </button>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </div>

    <!-- WhatsApp Budget Modal -->
    <div v-if="showBudgetModal" class="modal-overlay" @click="showBudgetModal = false">
      <div class="modal-container budget-settings-modal" @click.stop>
        <div class="modal-header">
          <h3>WhatsApp Budget Settings</h3>
          <button @click="showBudgetModal = false" class="modal-close-btn">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
          </button>
        </div>
        <div class="budget-modal-body">
          <!-- Monthly Budget -->
          <div class="form-group">
            <label>Monthly Budget (USD)</label>
            <p class="field-hint">Set your monthly spending cap for WhatsApp campaigns. Leave empty to remove the limit.</p>
            <div class="budget-input-wrap">
              <span class="budget-input-prefix">$</span>
              <input
                v-model="budgetForm.monthly_budget"
                type="number"
                min="0"
                step="0.01"
                class="budget-amount-input"
                placeholder="e.g. 500.00"
              />
              <span class="budget-input-suffix">USD / month</span>
            </div>
          </div>

          <!-- Market Selection -->
          <div class="form-group">
            <label>Your Audience Market</label>
            <p class="field-hint">Select the region where your recipients are located. This determines the per-message price used for cost estimates.</p>
            <select v-model="budgetForm.billing_market" class="form-select">
              <option v-for="market in waMarkets" :key="market" :value="market">{{ market }}</option>
            </select>
          </div>

          <!-- Live Pricing Reference -->
          <div v-if="budgetForm.billing_market" class="pricing-reference-box">
            <div class="pricing-ref-header">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style="color:#25D366"><path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z"/></svg>
              <span>Pricing for <strong>{{ budgetForm.billing_market }}</strong></span>
            </div>
            <div class="pricing-ref-rows">
              <div class="pricing-ref-row">
                <span class="pricing-type-chip pricing-type-chip--marketing">Marketing</span>
                <span class="pricing-ref-price">${{ (waPricing[budgetForm.billing_market] || waPricing['Other']).marketing.toFixed(4) }} <em>per message</em></span>
              </div>
              <div class="pricing-ref-row">
                <span class="pricing-type-chip pricing-type-chip--utility">Utility</span>
                <span class="pricing-ref-price">${{ (waPricing[budgetForm.billing_market] || waPricing['Other']).utility.toFixed(4) }} <em>per message</em></span>
              </div>
            </div>
            <p class="pricing-ref-source">Source: Meta WhatsApp API Pricing — July 2026</p>
          </div>

          <div class="form-actions" style="margin-top: 8px;">
            <button @click="showBudgetModal = false" class="cancel-btn">Cancel</button>
            <button @click="saveBudget" class="submit-btn" :disabled="savingBudget">
              <div v-if="savingBudget" class="loading-spinner-small"></div>
              <span v-else>Save Budget</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Notification Toast -->
    <div v-if="notification" class="notification-toast" :class="notification.type">
      <div class="notification-content">
        <svg v-if="notification.type === 'success'" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
        </svg>
        <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
        </svg>
        <div class="notification-text">
          <strong>{{ notification.title }}</strong>
          <p>{{ notification.message }}</p>
        </div>
      </div>
      <button @click="dismissNotification" class="notification-close">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
        </svg>
      </button>
    </div>
  </div>
  <QualibotChat />
</template>

<script>
import Sidebar from '../components/Sidebar.vue'
import TopHeader from '../components/TopHeader.vue'
import QualibotChat from '../components/QualibotChat.vue'
import { getUserTimezone, formatDateTime } from '../utils/timezone'

// WhatsApp API Pricing (July 2026) — per conversation in USD
const WA_PRICING = {
  Argentina: { marketing: 0.0618, utility: 0.0260 },
  Brazil: { marketing: 0.0625, utility: 0.0068 },
  Chile: { marketing: 0.0889, utility: 0.0200 },
  Colombia: { marketing: 0.0125, utility: 0.0008 },
  Egypt: { marketing: 0.0644, utility: 0.0036 },
  France: { marketing: 0.0859, utility: 0.0300 },
  Germany: { marketing: 0.1365, utility: 0.0550 },
  'Hong Kong': { marketing: 0.0732, utility: 0.0260 },
  Hungary: { marketing: 0.0860, utility: 0.0350 },
  India: { marketing: 0.0118, utility: 0.0014 },
  Indonesia: { marketing: 0.0411, utility: 0.0250 },
  Italy: { marketing: 0.0795, utility: 0.0300 },
  Malaysia: { marketing: 0.0860, utility: 0.0140 },
  Mexico: { marketing: 0.0305, utility: 0.0085 },
  Netherlands: { marketing: 0.1597, utility: 0.0500 },
  Nigeria: { marketing: 0.0516, utility: 0.0067 },
  Pakistan: { marketing: 0.0473, utility: 0.0100 },
  Peru: { marketing: 0.0703, utility: 0.0200 },
  Poland: { marketing: 0.0366, utility: 0.0122 },
  Qatar: { marketing: 0.0341, utility: 0.0120 },
  Romania: { marketing: 0.0860, utility: 0.0290 },
  Russia: { marketing: 0.0802, utility: 0.0400 },
  'Saudi Arabia': { marketing: 0.0501, utility: 0.0107 },
  Singapore: { marketing: 0.0732, utility: 0.0160 },
  'South Africa': { marketing: 0.0379, utility: 0.0076 },
  Spain: { marketing: 0.0707, utility: 0.0200 },
  Turkey: { marketing: 0.0109, utility: 0.0009 },
  'United Arab Emirates': { marketing: 0.0499, utility: 0.0157 },
  'United Kingdom': { marketing: 0.0635, utility: 0.0220 },
  'North America': { marketing: 0.0250, utility: 0.0034 },
  'Rest of Africa': { marketing: 0.0225, utility: 0.0040 },
  'Rest of Asia Pacific': { marketing: 0.0732, utility: 0.0113 },
  'Rest of Central & Eastern Europe': { marketing: 0.0860, utility: 0.0212 },
  'Rest of Latin America': { marketing: 0.0740, utility: 0.0113 },
  'Rest of Middle East': { marketing: 0.0341, utility: 0.0091 },
  'Rest of Western Europe': { marketing: 0.0592, utility: 0.0171 },
  Other: { marketing: 0.0604, utility: 0.0077 }
}

export default {
  name: 'WhatsAppBroadcastView',
  components: {
    Sidebar,
    TopHeader,
    QualibotChat
  },
  data () {
    return {
      whatsappStatus: null,
      loading: false,
      creating: false,
      showAudienceWarning: false,
      campaigns: [],
      approvedTemplates: [],
      categories: [],
      availableTags: [],
      allCustomers: [],
      activeTab: 'all',
      showCreateModal: false,
      notification: null,
      refreshInterval: null,
      stats: {
        total_sent: 0,
        delivery_rate: 0,
        active_campaigns: 0,
        total_recipients: 0
      },
      campaignForm: {
        campaign_name: '',
        template_name: '',
        template_language: '',
        target_type: 'all_customers',
        category_ids: [],
        custom_phone_numbers: [],
        description: '',
        use_dynamic_variables: false,
        variable_mapping: [],
        image_url: '',
        video_url: '',
        document_url: '',
        tag_ids: [],
        campaign_tag_ids: [],
        campaign_category_id: null,
        update_existing_category: false
      },
      phoneNumbersText: '',
      variableMappingCounter: 1,
      tabs: [
        { label: 'All', value: 'all', count: 0 },
        { label: 'Active', value: 'in_progress', count: 0 },
        { label: 'Completed', value: 'completed', count: 0 },
        { label: 'Draft', value: 'draft', count: 0 }
      ],
      // Excel/CSV Upload
      excelFile: null,
      excelData: null,
      excelTotalRows: 0,
      excelColumns: [],
      excelConfig: {
        phone_column: '',
        name_column: '',
        column_headers: {}
      },
      excelPhoneWarnings: null,
      uploadingExcel: false,
      showExcelPreview: false,
      // Customer Search for Custom List
      customerSearchQuery: '',
      customerSearchResults: [],
      searchingCustomers: false,
      selectedCustomers: [],
      searchDebounceTimer: null,
      // Media picker state
      mediaMode: 'upload', // 'upload' | 'existing' | 'url'
      mediaAssetUuid: null,
      mediaAssetName: null,
      previewMediaUrl: null,
      previewVideoError: false,
      previewImageError: false,
      mediaUploadLoading: false,
      mediaUploadError: null,
      mediaAssets: [],
      mediaAssetsLoading: false,
      // WhatsApp Budget
      budget: null,
      budgetLoading: false,
      showBudgetModal: false,
      budgetForm: { monthly_budget: '', billing_market: 'Other' },
      savingBudget: false,
      waPricing: WA_PRICING,
      waMarkets: Object.keys(WA_PRICING)
    }
  },
  computed: {
    filteredBroadcastMediaAssets () {
      const header = this.getHeaderComponent()
      if (!header || !header.format) return []
      const type = header.format.toLowerCase()
      return this.mediaAssets.filter(a => a.media_type === type)
    },
    broadcastMediaAcceptTypes () {
      const header = this.getHeaderComponent()
      if (!header || !header.format) return '*'
      if (header.format === 'VIDEO') return 'video/mp4'
      if (header.format === 'IMAGE') return 'image/jpeg,image/png'
      return 'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    },
    broadcastUrlExtensions () {
      const header = this.getHeaderComponent()
      if (!header || !header.format) return []
      if (header.format === 'VIDEO') return ['mp4']
      if (header.format === 'IMAGE') return ['jpg', 'png']
      return ['pdf', 'doc', 'docx']
    },
    broadcastUrlValid () {
      const field = this.getMediaUrlField()
      const url = (this.campaignForm[field] || '').toLowerCase().trim()
      if (!url) return null
      return this.broadcastUrlExtensions.some(ext => url.endsWith('.' + ext))
    },
    noCredentials () {
      return this.whatsappStatus !== null && !this.whatsappStatus.has_credentials
    },
    notVerified () {
      return this.whatsappStatus !== null &&
        this.whatsappStatus.has_credentials &&
        this.whatsappStatus.business_verification_status !== 'verified'
    },
    filteredCampaigns () {
      if (this.activeTab === 'all') {
        return this.campaigns
      }
      return this.campaigns.filter(c => c.status === this.activeTab)
    },
    selectedTemplate () {
      if (!this.campaignForm.template_name) return null
      return this.approvedTemplates.find(t => t.name === this.campaignForm.template_name)
    },
    totalRecipients () {
      if (this.campaignForm.target_type === 'all_customers') {
        return this.allCustomers.length
      } else if (this.campaignForm.target_type === 'by_category') {
        return this.categories
          .filter(cat => this.campaignForm.category_ids.includes(cat.id))
          .reduce((sum, cat) => sum + cat.customer_count, 0)
      } else if (this.campaignForm.target_type === 'by_tag') {
        const tagIds = this.campaignForm.tag_ids || []
        return this.availableTags
          .filter(tag => tagIds.includes(tag.id))
          .reduce((sum, tag) => sum + (tag.customer_count || 0), 0)
      } else if (this.campaignForm.target_type === 'custom_list') {
        return this.selectedCustomers.length
      } else if (this.campaignForm.target_type === 'excel_list') {
        return this.excelTotalRows || (this.excelData ? this.excelData.length : 0)
      }
      return 0
    },
    isFormValid () {
      // 1. Must have campaign name
      if (!this.campaignForm.campaign_name || this.campaignForm.campaign_name.trim() === '') {
        return false
      }

      // 2. Must have template selected
      if (!this.selectedTemplate) {
        return false
      }

      // 3. Must have target audience selected
      if (!this.campaignForm.target_type) {
        return false
      }

      // 4. Validate target audience specifics
      if (this.campaignForm.target_type === 'by_category') {
        // Must select at least one category
        if (!this.campaignForm.category_ids || this.campaignForm.category_ids.length === 0) {
          return false
        }
      } else if (this.campaignForm.target_type === 'custom_list') {
        // Must have at least one selected customer
        if (this.selectedCustomers.length === 0) {
          return false
        }
      } else if (this.campaignForm.target_type === 'excel_list') {
        // Must have uploaded Excel file
        if (!this.excelFile || !this.excelData || this.excelData.length === 0) {
          return false
        }
        // Must have selected phone column
        if (!this.excelConfig.phone_column) {
          return false
        }
        // Must have selected name column
        if (!this.excelConfig.name_column) {
          return false
        }
      }

      // 5. Check if template has media header and requires media
      if (this.hasMediaHeader()) {
        if (this.mediaMode !== 'url') {
          if (!this.mediaAssetUuid) return false
        } else {
          const mediaUrlField = this.getMediaUrlField()
          if (!this.campaignForm[mediaUrlField] || this.campaignForm[mediaUrlField].trim() === '') {
            return false
          }
          if (this.broadcastUrlValid !== true) return false
        }
      }

      // 6. All variables must be mapped
      const templateVariables = this.getAllTemplateVariables()
      if (templateVariables.length > 0) {
        for (const varIndex of templateVariables) {
          const mapping = this.campaignForm.variable_mapping.find(m => m.variable_index === varIndex)
          if (!mapping) {
            return false
          }

          // Determine source type
          const sourceType = mapping.source_type || (mapping.use_static ? 'static_value' : 'customer_field')

          // Check if mapping is complete based on source type
          if (sourceType === 'customer_field') {
            if (!mapping.customer_field) {
              return false
            }
          } else if (sourceType === 'static_value') {
            if (!mapping.static_value || mapping.static_value.trim() === '') {
              return false
            }
          } else if (sourceType === 'excel_column') {
            if (!mapping.excel_column) {
              return false
            }
          }
        }
      }

      // All validations passed
      return true
    },

    templatePricingType () {
      if (!this.selectedTemplate) return null
      const cat = (this.selectedTemplate.category || '').toUpperCase()
      if (cat === 'UTILITY' || cat === 'AUTHENTICATION') return 'utility'
      return 'marketing'
    },

    templatePricingLabel () {
      if (!this.selectedTemplate) return null
      const cat = (this.selectedTemplate.category || '').toUpperCase()
      if (cat === 'UTILITY') return 'Utility'
      if (cat === 'AUTHENTICATION') return 'Utility (Authentication)'
      return 'Marketing'
    },

    pricePerMessage () {
      if (!this.selectedTemplate || !this.budget) return null
      const market = (this.budget && this.budget.billing_market) || 'Other'
      const pricing = WA_PRICING[market] || WA_PRICING.Other
      return pricing[this.templatePricingType]
    },

    estimatedCampaignCost () {
      if (this.pricePerMessage === null || !this.totalRecipients) return null
      return this.pricePerMessage * this.totalRecipients
    },

    budgetRemaining () {
      if (!this.budget || !this.budget.monthly_budget) return null
      const spent = this.budget.spent_this_month || 0
      return Math.max(0, this.budget.monthly_budget - spent)
    },

    budgetUsagePercent () {
      if (!this.budget || !this.budget.monthly_budget) return 0
      const spent = this.budget.spent_this_month || 0
      return Math.min(100, Math.round((spent / this.budget.monthly_budget) * 100))
    },

    budgetAfterCampaign () {
      if (this.budgetRemaining === null || this.estimatedCampaignCost === null) return null
      return Math.max(0, this.budgetRemaining - this.estimatedCampaignCost)
    },

    // ─── Month calendar helpers ───────────────────────────────────────────────
    daysInMonth () {
      const now = new Date()
      return new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate()
    },
    daysElapsed () {
      return new Date().getDate()
    },
    daysRemaining () {
      return this.daysInMonth - this.daysElapsed
    },

    // ─── Projected month-end spend ────────────────────────────────────────────
    projectedMonthEndSpend () {
      if (!this.budget || !this.daysElapsed) return null
      const spent = this.budget.spent_this_month || 0
      if (spent === 0) return null
      return (spent / this.daysElapsed) * this.daysInMonth
    },

    // ─── Month-end nudge ─────────────────────────────────────────────────────
    showMonthEndNudge () {
      if (!this.budget || !this.budget.monthly_budget) return false
      if (this.daysRemaining > 5) return false
      return (this.budgetRemaining / this.budget.monthly_budget) > 0.25
    },

    // ─── Month-over-month diff ────────────────────────────────────────────────
    spendDiffPercent () {
      if (!this.budget) return null
      const prev = this.budget.spent_last_month || 0
      const curr = this.budget.spent_this_month || 0
      if (prev === 0) return null
      return Math.round(((curr - prev) / prev) * 100)
    },

    // ─── Budget utilization rates ─────────────────────────────────────────────
    lastMonthUtilizationPercent () {
      if (!this.budget || !this.budget.monthly_budget) return null
      const prev = this.budget.spent_last_month || 0
      return Math.min(100, Math.round((prev / this.budget.monthly_budget) * 100))
    },

    // ─── Remaining reach estimate ─────────────────────────────────────────────
    remainingReach () {
      if (!this.pricePerMessage || !this.budgetRemaining) return null
      return Math.floor(this.budgetRemaining / this.pricePerMessage)
    },

    // ─── Marketing vs Utility split ───────────────────────────────────────────
    marketingSpend () {
      return this.budget ? (this.budget.marketing_spend_this_month || 0) : 0
    },
    utilitySpend () {
      return this.budget ? (this.budget.utility_spend_this_month || 0) : 0
    },
    totalCategorizedSpend () {
      return this.marketingSpend + this.utilitySpend
    },
    marketingSpendPercent () {
      if (!this.totalCategorizedSpend) return 0
      return Math.round((this.marketingSpend / this.totalCategorizedSpend) * 100)
    },
    utilitySpendPercent () {
      if (!this.totalCategorizedSpend) return 0
      return 100 - this.marketingSpendPercent
    },

    // ─── History max for bar scaling ──────────────────────────────────────────
    historyMax () {
      if (!this.budget || !this.budget.monthly_history) return 1
      return Math.max(1, ...this.budget.monthly_history.map(m => m.spent))
    },

    // ─── Over-budget guard ────────────────────────────────────────────────────
    campaignExceedsBudget () {
      if (!this.budget || !this.budget.monthly_budget) return false
      if (this.estimatedCampaignCost === null) return false
      return this.estimatedCampaignCost > this.budgetRemaining
    }
  },
  async mounted () {
    await this.checkWhatsAppStatus()
    if (!this.noCredentials && !this.notVerified) {
      await Promise.all([this.loadData(), this.loadBudget()])
      this.startAutoRefresh()
    }
  },
  beforeUnmount () {
    this.stopAutoRefresh()
  },
  watch: {
    'campaignForm.video_url' () { this.previewVideoError = false },
    'campaignForm.image_url' () { this.previewImageError = false },
    previewMediaUrl () { this.previewVideoError = false; this.previewImageError = false }
  },
  methods: {
    async checkWhatsAppStatus () {
      try {
        const token = localStorage.getItem('auth_token') || localStorage.getItem('agent_token')
        const response = await fetch('https://api.quali-d.com/whatsapp/quick-status', {
          headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' }
        })
        if (response.ok) {
          this.whatsappStatus = await response.json()
        }
      } catch (_) {
        // Network error — don't block access
      }
    },

    formatDailyLimit (limit) {
      const map = {
        TIER_250: '250 messages / day',
        TIER_2K: '2,000 messages / day',
        TIER_10K: '10,000 messages / day',
        TIER_100K: '100,000 messages / day',
        TIER_UNLIMITED: 'Unlimited',
        UNKNOWN: 'Unknown'
      }
      return map[limit] || 'Unknown'
    },

    async loadData () {
      this.loading = true
      try {
        await Promise.all([
          this.loadCampaigns(),
          this.loadTemplates(),
          this.loadCategories(),
          this.loadAvailableTags()
        ])
      } catch (error) {
        console.error('Error loading data:', error)
      } finally {
        this.loading = false
      }
    },

    async loadCampaigns () {
      try {
        const token = localStorage.getItem('auth_token') || localStorage.getItem('agent_token')
        const response = await fetch('https://api.quali-d.com/whatsapp/broadcast/campaigns', {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json'
          }
        })

        const data = await response.json()

        if (data.success) {
          this.campaigns = data.campaigns || []

          // Log in-progress campaigns for debugging
          const inProgressCampaigns = this.campaigns.filter(c => c.status === 'in_progress')
          if (inProgressCampaigns.length > 0) {
            console.log('📊 In-progress campaigns:', inProgressCampaigns.map(c => ({
              id: c.id,
              name: c.campaign_name,
              progress: c.progress_percentage + '%',
              sent: c.sent_count,
              total: c.total_recipients,
              status: c.status
            })))
          }

          this.updateTabCounts()
          this.calculateStats()
        }
      } catch (error) {
        console.error('Error loading campaigns:', error)
      }
    },

    async loadTemplates () {
      try {
        const token = localStorage.getItem('auth_token') || localStorage.getItem('agent_token')
        const response = await fetch('https://api.quali-d.com/whatsapp/templates', {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json'
          }
        })

        const data = await response.json()

        if (data.success) {
          // Only show approved templates
          this.approvedTemplates = (data.templates || []).filter(t => t.status === 'APPROVED')
        }
      } catch (error) {
        console.error('Error loading templates:', error)
      }
    },

    async loadCategories () {
      try {
        const token = localStorage.getItem('auth_token') || localStorage.getItem('agent_token')
        const response = await fetch('https://api.quali-d.com/customers-categories', {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json'
          }
        })

        const data = await response.json()

        console.log('Categories API Response:', data)

        if (data.success) {
          this.categories = data.categories || []
          console.log('Categories loaded:', this.categories)
          console.log('First category customer_count:', this.categories[0]?.customer_count)
        } else {
          console.error('Categories API returned success=false:', data)
        }
      } catch (error) {
        console.error('Error loading categories:', error)
      }
    },

    async loadAvailableTags () {
      try {
        const token = localStorage.getItem('auth_token') || localStorage.getItem('agent_token')
        const response = await fetch('https://api.quali-d.com/customer-tags', {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json'
          }
        })
        const data = await response.json()
        this.availableTags = data.tags || []
      } catch (error) {
        console.error('Error loading tags:', error)
      }
    },

    async loadAllCustomers () {
      try {
        const token = localStorage.getItem('auth_token') || localStorage.getItem('agent_token')
        const response = await fetch('https://api.quali-d.com/customers/all', {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json'
          }
        })

        const data = await response.json()

        if (data.success) {
          this.allCustomers = data.data.customers || []
        }
      } catch (error) {
        console.error('Error loading all customers:', error)
      }
    },

    loadTemplateDetails () {
      const template = this.approvedTemplates.find(t => t.name === this.campaignForm.template_name)
      if (template) {
        this.campaignForm.template_language = template.language
      }
      // Reset media picker whenever the template changes
      this.mediaMode = 'upload'
      this.mediaAssetUuid = null
      this.mediaAssetName = null
      this.mediaUploadError = null
      this.campaignForm.image_url = ''
      this.campaignForm.video_url = ''
      this.campaignForm.document_url = ''
    },

    handleFormSubmit () {
      this.showAudienceWarning = true
    },

    async createCampaign () {
      try {
        this.creating = true

        // Validate Excel upload if using excel_list
        if (this.campaignForm.target_type === 'excel_list') {
          if (!this.excelData || !this.excelConfig.phone_column) {
            throw new Error('Please upload an Excel file and select the phone number column')
          }
        }

        // Auto-enable dynamic variables if template has variables
        const templateVariables = this.getAllTemplateVariables()
        if (templateVariables.length > 0) {
          this.campaignForm.use_dynamic_variables = true

          // Validation: Check if all template variables have been mapped
          const unmappedVariables = templateVariables.filter(varIndex => {
            const mapping = this.campaignForm.variable_mapping.find(m => m.variable_index === varIndex)
            if (!mapping) return true

            const sourceType = mapping.source_type || (mapping.use_static ? 'static_value' : 'customer_field')

            // Check based on source type
            if (sourceType === 'static_value') {
              return !mapping.static_value || mapping.static_value.trim() === ''
            } else if (sourceType === 'excel_column') {
              return !mapping.excel_column
            } else {
              // customer_field
              return !mapping.customer_field
            }
          })

          if (unmappedVariables.length > 0) {
            throw new Error(`Please map all template variables. Unmapped: ${unmappedVariables.map(v => `{{${v}}}`).join(', ')}`)
          }
        }

        const token = localStorage.getItem('auth_token') || localStorage.getItem('agent_token')

        // Prepare payload - filter out empty variable mappings
        const payload = { ...this.campaignForm }

        // Include pricing metadata so the backend can track monthly spend
        if (this.selectedTemplate) {
          payload.template_category = this.selectedTemplate.category || 'MARKETING'
        }
        if (this.estimatedCampaignCost !== null) {
          payload.campaign_cost = parseFloat(this.estimatedCampaignCost.toFixed(4))
        }

        // If using the media asset picker, send the UUID so the backend
        // can resolve the B2 URL server-side (the URL is never sent to the client)
        if (this.hasMediaHeader() && this.mediaMode !== 'url' && this.mediaAssetUuid) {
          payload.media_asset_uuid = this.mediaAssetUuid
          payload.image_url = ''
          payload.video_url = ''
          payload.document_url = ''
        }

        // Prepare Excel data if using excel_list
        if (this.campaignForm.target_type === 'excel_list') {
          const phoneCol = this.excelConfig.phone_column
          const nameCol = this.excelConfig.name_column
          const transformedExcelData = this.excelData.map(row => {
            return {
              ...row,
              phone_number: row[phoneCol],
              full_name: (nameCol && row[nameCol]) ? String(row[nameCol]).trim() : row[phoneCol]
            }
          })

          payload.excel_data = transformedExcelData
          payload.excel_config = this.excelConfig
        }

        // Only include variable mappings that have been actually filled in
        if (payload.variable_mapping && payload.variable_mapping.length > 0) {
          payload.variable_mapping = payload.variable_mapping.filter(mapping => {
            const sourceType = mapping.source_type || (mapping.use_static ? 'static_value' : 'customer_field')

            if (sourceType === 'static_value') {
              return mapping.static_value && mapping.static_value.trim() !== ''
            } else if (sourceType === 'excel_column') {
              return mapping.excel_column
            } else {
              // customer_field
              return mapping.customer_field
            }
          })
        }

        // Create campaign
        const createResponse = await fetch('https://api.quali-d.com/whatsapp/broadcast/campaigns', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            Accept: 'application/json'
          },
          body: JSON.stringify(payload)
        })

        const createData = await createResponse.json()

        if (!createResponse.ok || !createData.success) {
          throw new Error(createData.message || 'Failed to create campaign')
        }

        const campaignId = createData.campaign.id

        this.showNotification({
          type: 'success',
          title: 'Broadcast Started',
          message: `Campaign "${this.campaignForm.campaign_name}" is broadcasting to ${createData.campaign.total_recipients} recipients`
        })

        this.closeCreateModal()
        await this.loadCampaigns()

        // Switch to Active tab to see progress
        this.activeTab = 'in_progress'

        // Start sending campaign (don't wait for completion - run in background)
        fetch(`https://api.quali-d.com/whatsapp/broadcast/campaigns/${campaignId}/send`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json'
          }
        }).then(response => response.json()).then(sendData => {
          console.log('✅ Campaign broadcast finished:', sendData)
          // Refresh campaigns list one final time after completion
          this.loadCampaigns()
        }).catch(error => {
          console.error('❌ Error during campaign broadcast:', error)
          this.showNotification({
            type: 'error',
            title: 'Broadcast Error',
            message: 'An error occurred during broadcasting. Check the campaign status.'
          })
          this.loadCampaigns()
        })
      } catch (error) {
        console.error('Error creating campaign:', error)
        this.showNotification({
          type: 'error',
          title: 'Error',
          message: error.message || 'Failed to create campaign'
        })
      } finally {
        this.creating = false
      }
    },

    async openCreateModal () {
      this.showCreateModal = true
      await this.loadAllCustomers()
    },

    closeCreateModal () {
      this.showCreateModal = false
      this.showAudienceWarning = false
      this.resetForm()
    },

    resetForm () {
      this.campaignForm = {
        campaign_name: '',
        template_name: '',
        template_language: '',
        target_type: 'all_customers',
        category_ids: [],
        tag_ids: [],
        custom_phone_numbers: [],
        description: '',
        use_dynamic_variables: false,
        variable_mapping: [],
        campaign_tag_ids: [],
        campaign_category_id: null,
        update_existing_category: false
      }
      this.phoneNumbersText = ''
      this.variableMappingCounter = 1
      this.clearExcelFile()
    },

    async uploadExcelFile (event) {
      const file = event.target.files[0]
      if (!file) return

      this.uploadingExcel = true
      const formData = new FormData()
      formData.append('excel_file', file)

      try {
        const token = localStorage.getItem('auth_token') || localStorage.getItem('agent_token')
        const response = await fetch('https://api.quali-d.com/whatsapp/broadcast/parse-excel', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`
          },
          body: formData
        })

        const result = await response.json()

        if (result.success) {
          this.excelFile = file
          this.excelColumns = result.columns
          this.excelData = result.data // full dataset - used for campaign creation
          this.excelTotalRows = result.total_rows
          this.excelPhoneWarnings = result.phone_validation || null
          this.showExcelPreview = true

          // Auto-detect phone column by header keywords
          const phoneCol = this.excelColumns.find(col =>
            col.label.toLowerCase().includes('phone') ||
            col.label.toLowerCase().includes('mobile') ||
            col.label.toLowerCase().includes('number') ||
            col.label.toLowerCase().includes('tel')
          )
          if (phoneCol) {
            this.excelConfig.phone_column = phoneCol.letter
          }

          // Auto-detect name column by header keywords
          const nameCol = this.excelColumns.find(col =>
            col.label.toLowerCase().includes('name') ||
            col.label.toLowerCase().includes('customer') ||
            col.label.toLowerCase().includes('client') ||
            col.label.toLowerCase().includes('contact')
          )
          if (nameCol) {
            this.excelConfig.name_column = nameCol.letter
          }

          this.$toast.success(`Excel parsed: ${result.total_rows} rows found`)
        } else {
          this.$toast.error(result.message || 'Failed to parse Excel file')
        }
      } catch (error) {
        console.error('Excel upload error:', error)
        this.$toast.error('Error uploading Excel file')
      } finally {
        this.uploadingExcel = false
      }
    },

    clearExcelFile () {
      this.excelFile = null
      this.excelData = null
      this.excelTotalRows = 0
      this.excelColumns = []
      this.excelConfig = { phone_column: '', name_column: '', column_headers: {} }
      this.excelPhoneWarnings = null
      this.showExcelPreview = false
      // Clear file input if it exists
      if (this.$refs.excelFileInput) {
        this.$refs.excelFileInput.value = ''
      }
    },

    viewCampaignDetails (campaign) {
      this.$router.push('/whatsapp-broadcast/' + campaign.id)
    },

    calculateCampaignDeliveryRate (campaign) {
      if (!campaign || !campaign.total_recipients || campaign.total_recipients === 0) {
        return 0
      }
      const sent = campaign.sent_count || 0
      return Math.round((sent / campaign.total_recipients) * 100)
    },

    async deleteCampaign (campaign) {
      if (!confirm(`Are you sure you want to delete "${campaign.campaign_name}"?`)) {
        return
      }

      try {
        const token = localStorage.getItem('auth_token') || localStorage.getItem('agent_token')
        const response = await fetch(`https://api.quali-d.com/whatsapp/broadcast/campaigns/${campaign.id}/cancel`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json'
          }
        })

        const data = await response.json()

        if (data.success) {
          this.showNotification({
            type: 'success',
            title: 'Campaign Deleted',
            message: `Campaign "${campaign.campaign_name}" has been deleted`
          })
          await this.loadCampaigns()
        }
      } catch (error) {
        console.error('Error deleting campaign:', error)
        this.showNotification({
          type: 'error',
          title: 'Error',
          message: 'Failed to delete campaign'
        })
      }
    },

    updateTabCounts () {
      this.tabs[0].count = this.campaigns.length
      this.tabs[1].count = this.campaigns.filter(c => c.status === 'in_progress').length
      this.tabs[2].count = this.campaigns.filter(c => c.status === 'completed').length
      this.tabs[3].count = this.campaigns.filter(c => c.status === 'draft').length
    },

    calculateStats () {
      const completed = this.campaigns.filter(c => c.status === 'completed')

      this.stats.total_sent = completed.reduce((sum, c) => sum + (c.sent_count || 0), 0)
      this.stats.active_campaigns = this.campaigns.filter(c => c.status === 'in_progress').length
      this.stats.total_recipients = this.campaigns.reduce((sum, c) => sum + (c.total_recipients || 0), 0)

      // Calculate delivery rate: sent_count / total_recipients for completed campaigns
      const completedTotalRecipients = completed.reduce((sum, c) => sum + (c.total_recipients || 0), 0)
      this.stats.delivery_rate = completedTotalRecipients > 0
        ? Math.round((this.stats.total_sent / completedTotalRecipients) * 100)
        : 0
    },

    startAutoRefresh () {
      // Refresh active campaigns every 5 seconds for better UX
      this.refreshInterval = setInterval(() => {
        const hasActiveCampaigns = this.campaigns.some(c => c.status === 'in_progress')
        if (hasActiveCampaigns) {
          console.log('🔄 Auto-refreshing campaigns... Active campaigns found:', hasActiveCampaigns)
          this.loadCampaigns()
        } else {
          console.log('⏸️ No active campaigns, skipping refresh')
        }
      }, 5000) // Changed to 5 seconds for faster updates
    },

    stopAutoRefresh () {
      if (this.refreshInterval) {
        clearInterval(this.refreshInterval)
      }
    },

    formatDate (dateString) {
      return formatDateTime(dateString, getUserTimezone())
    },

    getEmptyStateMessage () {
      const messages = {
        all: 'Create your first campaign to get started',
        in_progress: 'No campaigns are currently running',
        completed: 'No completed campaigns yet',
        draft: 'No draft campaigns'
      }
      return messages[this.activeTab]
    },

    showNotification (notification) {
      this.notification = notification
      setTimeout(() => {
        this.notification = null
      }, 5000)
    },

    dismissNotification () {
      this.notification = null
    },

    // ─── WhatsApp Budget ─────────────────────────────────────────────────────
    async loadBudget () {
      try {
        this.budgetLoading = true
        const token = localStorage.getItem('auth_token') || localStorage.getItem('agent_token')
        const response = await fetch('https://api.quali-d.com/whatsapp/broadcast/budget', {
          headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' }
        })
        if (response.ok) {
          const data = await response.json()
          if (data.success) {
            this.budget = data.budget
            this.budgetForm.monthly_budget = data.budget.monthly_budget != null ? data.budget.monthly_budget : ''
            this.budgetForm.billing_market = data.budget.billing_market || 'Other'
          }
        }
      } catch (e) {
        console.error('Error loading budget:', e)
      } finally {
        this.budgetLoading = false
      }
    },

    openBudgetModal () {
      if (this.budget) {
        this.budgetForm.monthly_budget = this.budget.monthly_budget != null ? this.budget.monthly_budget : ''
        this.budgetForm.billing_market = this.budget.billing_market || 'Other'
      }
      this.showBudgetModal = true
    },

    async saveBudget () {
      try {
        this.savingBudget = true
        const token = localStorage.getItem('auth_token') || localStorage.getItem('agent_token')
        const payload = {
          monthly_budget: this.budgetForm.monthly_budget !== '' ? parseFloat(this.budgetForm.monthly_budget) : null,
          billing_market: this.budgetForm.billing_market || 'Other'
        }
        const response = await fetch('https://api.quali-d.com/whatsapp/broadcast/budget', {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            Accept: 'application/json'
          },
          body: JSON.stringify(payload)
        })
        const data = await response.json()
        if (data.success) {
          this.budget = data.budget
          this.showBudgetModal = false
          this.showNotification({
            type: 'success',
            title: 'Budget Saved',
            message: 'Your WhatsApp budget settings have been updated.'
          })
        }
      } catch (e) {
        console.error('Error saving budget:', e)
        this.showNotification({ type: 'error', title: 'Error', message: 'Failed to save budget settings.' })
      } finally {
        this.savingBudget = false
      }
    },

    formatUSD (value) {
      if (value === null || value === undefined) return '—'
      const num = parseFloat(value)
      // Always 4 decimal places, strip trailing zeros after the 2nd decimal
      // e.g. 499.0036 → $499.0036 | 499.00 → $499.00 | 0.50 → $0.50
      return '$' + num.toFixed(4).replace(/(\.\d{2})0+$/, '$1')
    },

    // Dynamic Variable Mapping Methods
    hasVariables (text) {
      if (!text) return false
      return /\{\{(\d+)\}\}/.test(text)
    },

    extractVariables (text) {
      if (!text) return []
      const matches = text.match(/\{\{(\d+)\}\}/g)
      if (!matches) return []
      return [...new Set(matches.map(m => m.replace(/[{}]/g, '')))]
    },

    getOrCreateMapping (varIndex) {
      let mapping = this.campaignForm.variable_mapping.find(m => m.variable_index === varIndex)
      if (!mapping) {
        mapping = {
          variable_index: varIndex,
          source_type: 'customer_field', // NEW: default source type
          customer_field: '',
          static_value: '',
          excel_column: '', // NEW
          default_value: '',
          use_static: false // Keep for backwards compatibility
        }
        this.campaignForm.variable_mapping.push(mapping)
      }
      // Ensure source_type exists (for backwards compatibility)
      if (!mapping.source_type) {
        mapping.source_type = mapping.use_static ? 'static_value' : 'customer_field'
      }
      return mapping
    },

    addVariableMapping () {
      this.campaignForm.variable_mapping.push({
        variable_index: String(this.variableMappingCounter),
        customer_field: '',
        default_value: ''
      })
      this.variableMappingCounter++
    },

    removeVariableMapping (index) {
      this.campaignForm.variable_mapping.splice(index, 1)
    },

    formatVariable (varIndex) {
      return `{{${varIndex}}}`
    },

    getFieldExample (fieldName) {
      const examples = {
        full_name: 'Ahmed Mohamed',
        phone_number: '+201234567890',
        email: 'ahmed@example.com',
        category_name: 'VIP Customer',
        category_id: '5',
        city: 'Cairo',
        country: 'Egypt',
        address: '123 Main St',
        created_at: '2025-01-15',
        id: '123'
      }
      return examples[fieldName] || fieldName
    },

    getHeaderComponent () {
      if (!this.selectedTemplate) return null
      return this.selectedTemplate.components.find(c => c.type === 'HEADER')
    },

    getBodyComponent () {
      if (!this.selectedTemplate) return null
      return this.selectedTemplate.components.find(c => c.type === 'BODY')
    },

    getFooterComponent () {
      if (!this.selectedTemplate) return null
      return this.selectedTemplate.components.find(c => c.type === 'FOOTER')
    },

    getButtonsComponent () {
      if (!this.selectedTemplate) return null
      return this.selectedTemplate.components.find(c => c.type === 'BUTTONS')
    },

    replaceVariablesInText (text) {
      if (!text) return ''
      let result = text

      // Replace variables with mapped values
      this.campaignForm.variable_mapping.forEach(mapping => {
        const variable = `{{${mapping.variable_index}}}`
        let value = ''

        // Determine source type (backwards compatible)
        const sourceType = mapping.source_type || (mapping.use_static ? 'static_value' : 'customer_field')

        if (sourceType === 'static_value') {
          // Use static value directly
          value = mapping.static_value || variable
        } else if (sourceType === 'excel_column') {
          // Use first row value from Excel column
          value = this.getExcelColumnPreview(mapping.excel_column) || mapping.default_value || variable
        } else if (sourceType === 'customer_field') {
          // Use customer field - check if we have actual customer data first
          if (this.campaignForm.target_type === 'custom_list' && this.selectedCustomers.length > 0) {
            // Use first selected customer's actual data
            value = this.getCustomerFieldValue(this.selectedCustomers[0], mapping.customer_field) || mapping.default_value || variable
          } else {
            // Use example value
            value = mapping.customer_field ? this.getFieldExample(mapping.customer_field) : (mapping.default_value || variable)
          }
        } else {
          // No mapping - show variable placeholder
          value = variable
        }

        result = result.replace(new RegExp(variable.replace(/[{}]/g, '\\$&'), 'g'), value)
      })

      return result
    },

    getExcelColumnPreview (columnLetter) {
      if (!columnLetter || !this.excelData || this.excelData.length === 0) {
        return null
      }

      // Get first row of data
      const firstRow = this.excelData[0]
      if (!firstRow || !firstRow[columnLetter]) {
        return null
      }

      return firstRow[columnLetter]
    },

    getCustomerFieldValue (customer, fieldName) {
      if (!customer || !fieldName) {
        return null
      }

      // Special handling for created_at date formatting
      if (fieldName === 'created_at' && customer[fieldName]) {
        const date = new Date(customer[fieldName])
        return date.toLocaleDateString()
      }

      // Return the field value from the customer object
      return customer[fieldName] || null
    },

    generatePreviewText (customerName, customerCategory) {
      if (!this.selectedTemplate) return ''

      const bodyComponent = this.selectedTemplate.components.find(c => c.type === 'BODY')
      if (!bodyComponent || !bodyComponent.text) return ''

      return this.replaceVariablesInText(bodyComponent.text)
    },

    areAllVariablesMapped () {
      const templateVariables = this.getAllTemplateVariables()
      if (templateVariables.length === 0) {
        return true
      }

      for (const varIndex of templateVariables) {
        const mapping = this.campaignForm.variable_mapping.find(m => m.variable_index === varIndex)
        if (!mapping) {
          return false
        }

        const sourceType = mapping.source_type || (mapping.use_static ? 'static_value' : 'customer_field')

        if (sourceType === 'customer_field' && !mapping.customer_field) {
          return false
        } else if (sourceType === 'static_value' && (!mapping.static_value || mapping.static_value.trim() === '')) {
          return false
        } else if (sourceType === 'excel_column' && !mapping.excel_column) {
          return false
        }
      }

      return true
    },

    getAllTemplateVariables () {
      if (!this.selectedTemplate) return []
      const allVariables = []

      this.selectedTemplate.components.forEach(component => {
        // HEADER component
        if (component.type === 'HEADER' && component.text) {
          const variables = this.extractVariables(component.text)
          variables.forEach(v => {
            allVariables.push('header')
          })
        }

        // BODY component
        if (component.type === 'BODY') {
          if (component.text) {
            const variables = this.extractVariables(component.text)
            allVariables.push(...variables)
          }
          // Also check example.body_text
          if (component.example?.body_text && Array.isArray(component.example.body_text)) {
            for (let i = 0; i < component.example.body_text.length; i++) {
              allVariables.push(String(i + 1))
            }
          }
        }

        // BUTTONS component - variables in button URLs need "button_" prefix
        if (component.type === 'BUTTONS' && component.buttons && Array.isArray(component.buttons)) {
          component.buttons.forEach((button, buttonIndex) => {
            if (button.url) {
              const urlVariables = this.extractVariables(button.url)
              urlVariables.forEach(v => {
                allVariables.push(`button_${v}`)
              })
            }
            if (button.text) {
              const textVariables = this.extractVariables(button.text)
              textVariables.forEach(v => {
                allVariables.push(`button_${v}`)
              })
            }
          })
        }
      })

      // Return unique variables sorted
      const unique = [...new Set(allVariables)]
      return unique.sort((a, b) => {
        // Sort: header first, then numbers, then button_numbers
        if (a === 'header') return -1
        if (b === 'header') return 1
        const aNum = parseInt(a.replace('button_', ''))
        const bNum = parseInt(b.replace('button_', ''))
        return aNum - bNum
      })
    },

    // Phone Number Management Methods
    // Customer Search Methods
    searchCustomers () {
      // Debounce search
      clearTimeout(this.searchDebounceTimer)

      if (!this.customerSearchQuery || this.customerSearchQuery.trim().length < 2) {
        this.customerSearchResults = []
        return
      }

      this.searchDebounceTimer = setTimeout(async () => {
        this.searchingCustomers = true
        try {
          const token = localStorage.getItem('auth_token') || localStorage.getItem('agent_token')
          const response = await fetch(`https://api.quali-d.com/customers?page=1&per_page=15&search_name=${encodeURIComponent(this.customerSearchQuery)}`, {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: 'application/json'
            }
          })

          const data = await response.json()

          if (data.success && data.data && data.data.customers) {
            // Filter out already selected customers
            this.customerSearchResults = data.data.customers.filter(customer =>
              !this.selectedCustomers.some(selected => selected.id === customer.id)
            )
          } else {
            this.customerSearchResults = []
          }
        } catch (error) {
          console.error('Error searching customers:', error)
          this.$toast.error('Error searching customers')
        } finally {
          this.searchingCustomers = false
        }
      }, 300)
    },

    addCustomerToList (customer) {
      // Check if already added
      if (this.selectedCustomers.some(c => c.id === customer.id)) {
        this.$toast.warning('Customer already added')
        return
      }

      this.selectedCustomers.push(customer)
      this.customerSearchQuery = ''
      this.customerSearchResults = []

      // Update campaignForm.custom_phone_numbers for backend compatibility
      this.updateCustomPhoneNumbers()

      this.$toast.success(`${customer.full_name} added`)
    },

    removeCustomerFromList (index) {
      const customer = this.selectedCustomers[index]
      this.selectedCustomers.splice(index, 1)

      // Update campaignForm.custom_phone_numbers for backend compatibility
      this.updateCustomPhoneNumbers()

      this.$toast.success(`${customer.full_name} removed`)
    },

    updateCustomPhoneNumbers () {
      // Sync selectedCustomers with campaignForm.custom_phone_numbers
      this.campaignForm.custom_phone_numbers = this.selectedCustomers.map(c => c.phone_number)
    },

    // ── Broadcast Media Picker ────────────────────────────────────────────────

    setBroadcastMediaMode (mode) {
      this.mediaMode = mode
      this.mediaAssetUuid = null
      this.mediaAssetName = null
      this.previewMediaUrl = null
      this.mediaUploadError = null
      this.campaignForm.image_url = ''
      this.campaignForm.video_url = ''
      this.campaignForm.document_url = ''
      if (mode === 'existing') this.loadBroadcastMediaAssets()
    },

    async loadBroadcastMediaAssets () {
      try {
        this.mediaAssetsLoading = true
        const token = localStorage.getItem('auth_token') || localStorage.getItem('agent_token')
        const header = this.getHeaderComponent()
        const type = header ? header.format.toLowerCase() : ''
        const response = await fetch(`https://api.quali-d.com/whatsapp/broadcast/media?type=${type}`, {
          headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' }
        })
        const data = await response.json()
        this.mediaAssets = data.success ? (data.assets || []) : []
      } catch {
        this.mediaAssets = []
      } finally {
        this.mediaAssetsLoading = false
      }
    },

    async onBroadcastMediaSelected (e) {
      const file = e.target.files[0]
      if (!file) return
      this.mediaUploadError = null
      this.mediaUploadLoading = true
      try {
        const token = localStorage.getItem('auth_token') || localStorage.getItem('agent_token')
        const header = this.getHeaderComponent()
        const formData = new FormData()
        formData.append('file', file)
        formData.append('media_type', header ? header.format.toLowerCase() : '')
        const response = await fetch('https://api.quali-d.com/whatsapp/broadcast/media/upload', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
          body: formData
        })
        const data = await response.json()
        if (!response.ok || !data.success) throw new Error(data.message || 'Upload failed')
        this.mediaAssetUuid = data.asset.uuid
        this.mediaAssetName = data.asset.original_filename
        this.previewMediaUrl = data.asset.preview_url || null
      } catch (err) {
        this.mediaUploadError = err.message || 'Upload failed. Please try again.'
      } finally {
        this.mediaUploadLoading = false
        e.target.value = ''
      }
    },

    selectBroadcastMediaAsset (asset) {
      this.mediaAssetUuid = asset.uuid
      this.mediaAssetName = asset.original_filename
      this.previewMediaUrl = asset.preview_url || null
    },

    clearBroadcastMedia () {
      this.mediaAssetUuid = null
      this.mediaAssetName = null
      this.previewMediaUrl = null
      this.mediaUploadError = null
      if (this.$refs.broadcastMediaFile) this.$refs.broadcastMediaFile.value = ''
    },

    formatFileSize (bytes) {
      if (bytes < 1024) return bytes + ' B'
      if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB'
      return (bytes / 1048576).toFixed(1) + ' MB'
    },

    // ─────────────────────────────────────────────────────────────────────────

    // Media Header Methods
    hasMediaHeader () {
      const header = this.getHeaderComponent()
      return header && header.format && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(header.format)
    },

    getMediaHeaderType () {
      const header = this.getHeaderComponent()
      if (!header || !header.format) return ''
      return header.format.charAt(0) + header.format.slice(1).toLowerCase() // IMAGE -> Image
    },

    getMediaUrlField () {
      const header = this.getHeaderComponent()
      if (!header || !header.format) return ''
      return header.format.toLowerCase() + '_url' // IMAGE -> image_url
    },

    getDocumentName () {
      if (!this.campaignForm.document_url) return 'Document'
      const url = this.campaignForm.document_url
      const parts = url.split('/')
      const filename = parts[parts.length - 1]
      return filename || 'Document'
    },

    handleImageError () {
      this.previewImageError = true
    },

    handleVideoError () {
      this.previewVideoError = true
    }
  }
}
</script>

<style scoped>
.broadcast-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-primary);
}

.main-content {
  flex: 1;
  margin-left: 280px;
  display: flex;
  flex-direction: column;
}

.page-content {
  flex: 1;
  padding: 24px 32px;
  overflow-y: auto;
}

.broadcast-container {
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
}

/* Page Header */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
}

.header-content h1 {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 8px 0;
}

.subtitle {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0;
}

.create-campaign-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #25D366;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.create-campaign-btn:hover {
  background: #20c05a;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(37, 211, 102, 0.3);
}

/* Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
}

.stat-card {
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-content {
  flex: 1;
}

.stat-value {
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1.2;
}

.stat-label {
  font-size: 13px;
  color: var(--text-secondary);
  margin-top: 4px;
}

/* Campaigns Section */
.campaigns-section {
  background: var(--bg-primary);
  border-radius: 12px;
  overflow: hidden;
}

.tabs-header {
  display: flex;
  border-bottom: 1px solid var(--border-primary);
  padding: 0 20px;
  gap: 8px;
}

.tab-btn {
  padding: 16px 20px;
  background: transparent;
  border: none;
  border-bottom: 3px solid transparent;
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
}

.tab-btn.active {
  color: #25D366;
  border-bottom-color: #25D366;
}

.tab-count {
  padding: 2px 8px;
  background: var(--bg-tertiary);
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.tab-btn.active .tab-count {
  background: rgba(37, 211, 102, 0.1);
  color: #25D366;
}

/* Loading & Empty States */
.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
}

.loading-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid var(--border-primary);
  border-top-color: #25D366;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.empty-state svg {
  color: var(--text-tertiary);
  margin-bottom: 16px;
}

.empty-state h3 {
  font-size: 18px;
  color: var(--text-primary);
  margin: 0 0 8px 0;
}

.empty-state p {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0;
}

/* ── Campaigns Table ─────────────────────────────────────── */
.campaigns-table-wrapper {
  overflow-x: auto;
}

.campaigns-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13.5px;
}

/* Header */
.campaigns-table thead tr {
  background: var(--bg-tertiary);
}

.campaigns-table thead th {
  padding: 12px 18px;
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.07em;
  white-space: nowrap;
  border-top: 1px solid var(--border-primary);
  border-bottom: 2px solid var(--border-primary);
}

.campaigns-table thead th:first-child {
  border-left: 1px solid var(--border-primary);
  border-radius: 10px 0 0 0;
}

.campaigns-table thead th:last-child {
  border-right: 1px solid var(--border-primary);
  border-radius: 0 10px 0 0;
}

.campaigns-table thead th.col-num    { text-align: right; }
.campaigns-table thead th.col-actions { text-align: center; }

/* Vertical separators — header */
.campaigns-table thead th + th {
  border-left: 1px solid var(--border-primary);
}

/* Body rows */
.campaign-row td {
  padding: 13px 18px;
  border-bottom: 1px solid var(--border-primary);
  color: var(--text-primary);
  vertical-align: middle;
  background: var(--bg-primary);
  transition: background 0.15s ease, color 0.15s ease;
}

.campaign-row td:first-child {
  border-left: 3px solid transparent;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.campaign-row td:last-child {
  border-right: 1px solid var(--border-primary);
}

.campaign-row:last-child td:first-child  { border-radius: 0 0 0 10px; }
.campaign-row:last-child td:last-child   { border-radius: 0 0 10px 0; }
.campaign-row:last-child td              { border-bottom: 1px solid var(--border-primary); }

/* Vertical separators — body */
.campaign-row td + td {
  border-left: 1px solid var(--border-primary);
}

/* Hover */
.campaign-row:hover td {
  background: rgba(37, 211, 102, 0.04);
}

.campaign-row:hover td:first-child {
  border-left-color: #25D366;
}

/* Campaign name */
.campaign-name-cell {
  font-weight: 600;
  white-space: nowrap;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 160px;
}

/* Template */
.template-cell {
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  min-width: 140px;
}

.lang-chip {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 4px;
  background: var(--bg-tertiary);
  color: var(--text-secondary);
  letter-spacing: 0.04em;
  border: 1px solid var(--border-primary);
  flex-shrink: 0;
}

/* Numeric cells */
.num-cell {
  text-align: right;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 500;
}

.read-cell   { color: #8B5CF6; font-weight: 700; }
.failed-cell { color: #EF4444; font-weight: 600; }

/* Creator */
.creator-cell { white-space: nowrap; }
.creator-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 99px;
  font-size: 11px;
  font-weight: 600;
}
.creator-badge--owner {
  background: var(--border-primary);
  color: var(--text-secondary);
}
.creator-badge--agent {
  background: rgba(14,165,233,.12);
  color: #0369a1;
}

/* Date */
.date-cell {
  white-space: nowrap;
  color: var(--text-secondary);
  font-size: 12px;
}

/* Actions */
.col-actions {
  text-align: center;
  white-space: nowrap;
}

.action-btns {
  display: flex;
  gap: 6px;
  justify-content: center;
}

/* Inline progress (in_progress rows) */
.inline-progress {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
}

.inline-progress-bar {
  flex: 1;
  height: 4px;
  background: var(--bg-tertiary);
  border-radius: 2px;
  overflow: hidden;
  min-width: 60px;
}

.inline-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #25D366, #20c05a);
  border-radius: 2px;
  transition: width 0.4s ease;
}

.inline-progress-pct {
  font-size: 11px;
  font-weight: 600;
  color: #25D366;
  white-space: nowrap;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: capitalize;
}

.status-badge.draft {
  background: rgba(107, 114, 128, 0.1);
  color: #6B7280;
}

.status-badge.in_progress {
  background: rgba(59, 130, 246, 0.1);
  color: #3B82F6;
}

.status-badge.completed {
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.15), rgba(34, 197, 94, 0.05));
  color: #22C55E;
  border: 1px solid rgba(34, 197, 94, 0.3);
  font-weight: 700;
}

.status-badge.failed {
  background: rgba(239, 68, 68, 0.1);
  color: #EF4444;
}

.action-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
}

.action-btn:hover {
  background: var(--bg-tertiary);
  border-color: var(--border-secondary);
}

.delete-btn:hover {
  border-color: #EF4444;
  color: #EF4444;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-container {
  background: var(--bg-secondary);
  border-radius: 16px;
  width: 100%;
  max-width: 1200px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: relative;
}

.modal-header {
  padding: 24px;
  border-bottom: 1px solid var(--border-primary);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.modal-close-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-tertiary);
  border: none;
  border-radius: 6px;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
}

.modal-close-btn:hover {
  background: var(--bg-primary);
  color: var(--text-primary);
}

.modal-content {
  padding: 24px;
  overflow-y: auto;
}

.campaign-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-secondary);
}

.required {
  color: #EF4444;
}

.form-input,
.form-select,
.form-textarea {
  padding: 12px 16px;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  background: var(--bg-primary);
  color: var(--text-primary);
  font-size: 14px;
  font-family: inherit;
  transition: all 0.2s ease;
}

.form-input:focus,
.form-select:focus,
.form-textarea:focus {
  outline: none;
  border-color: #25D366;
  box-shadow: 0 0 0 3px rgba(37, 211, 102, 0.1);
}

.form-textarea {
  resize: vertical;
  font-family: inherit;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.radio-group,
.checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.radio-label,
.checkbox-label {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.radio-label:hover,
.checkbox-label:hover {
  border-color: #25D366;
  background: rgba(37, 211, 102, 0.02);
}

.radio-label input,
.checkbox-label input {
  cursor: pointer;
}

.field-hint {
  font-size: 12px;
  color: var(--text-tertiary);
  margin: 0;
}

/* ── Campaign enrichment panel (tag + category assignment) ── */
.campaign-enrichment-panel {
  background: rgba(99, 102, 241, 0.05);
  border: 1px solid rgba(99, 102, 241, 0.22);
  border-radius: 12px;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.enrichment-panel-header {
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding-bottom: 14px;
  border-bottom: 1px solid rgba(99, 102, 241, 0.15);
}

.enrichment-badge {
  display: inline-block;
  background: #6366F1;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  padding: 2px 9px;
  border-radius: 99px;
  width: fit-content;
}

.enrichment-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}

.enrichment-subtitle {
  font-size: 12px;
  color: var(--text-tertiary);
  line-height: 1.5;
}

.enrichment-select:focus {
  border-color: #6366F1 !important;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.12) !important;
}

.enrichment-empty-hint {
  color: #F59E0B !important;
}

.enrichment-checkbox {
  border-color: rgba(99, 102, 241, 0.25) !important;
  background: rgba(99, 102, 241, 0.03) !important;
}

.enrichment-checkbox:hover {
  border-color: #6366F1 !important;
  background: rgba(99, 102, 241, 0.06) !important;
}

.tag-checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
}

.tag-color-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

/* ── Audience confirmation dialog ───────────────────────────────────────── */
.confirm-dialog-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: inherit;
  z-index: 20;
  backdrop-filter: blur(2px);
}
.confirm-dialog {
  background: var(--bg-card);
  border-radius: 14px;
  padding: 32px 28px 24px;
  width: 380px;
  max-width: 90%;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.35);
  text-align: center;
  border-top: 4px solid #f59e0b;
}
.confirm-dialog--danger {
  border-top-color: #ef4444;
}
.confirm-dialog-icon-row {
  display: flex;
  justify-content: center;
  margin-bottom: 14px;
}
.confirm-dialog-icon-wrap {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: rgba(245, 158, 11, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #f59e0b;
}
.confirm-dialog--danger .confirm-dialog-icon-wrap {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}
.confirm-dialog-title {
  font-size: 17px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 12px;
}
.confirm-dialog-body {
  font-size: 14px;
  color: var(--text-secondary);
  line-height: 1.6;
  margin: 0 0 10px;
}
.confirm-dialog-caution {
  font-size: 12px;
  color: #ef4444;
  background: rgba(239, 68, 68, 0.07);
  border-radius: 6px;
  padding: 8px 12px;
  margin: 0 0 20px;
  line-height: 1.5;
}
.confirm-dialog-actions {
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-top: 20px;
}
.confirm-dialog-cancel {
  flex: 1;
  padding: 10px 16px;
  border-radius: 8px;
  border: 1px solid var(--border-secondary);
  background: transparent;
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
}
.confirm-dialog-cancel:hover {
  background: var(--bg-secondary);
  color: var(--text-primary);
}
.confirm-dialog-confirm {
  flex: 1;
  padding: 10px 16px;
  border-radius: 8px;
  border: none;
  background: #f59e0b;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
}
.confirm-dialog-confirm:hover { background: #d97706; }
.confirm-dialog--danger .confirm-dialog-confirm { background: #ef4444; }
.confirm-dialog--danger .confirm-dialog-confirm:hover { background: #dc2626; }
.confirm-fade-enter-active, .confirm-fade-leave-active { transition: opacity 0.18s ease; }
.confirm-fade-enter-from, .confirm-fade-leave-to { opacity: 0; }

.form-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 8px;
  padding-top: 20px;
  border-top: 1px solid var(--border-primary);
}

.cancel-btn,
.submit-btn {
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.cancel-btn {
  background: transparent;
  border: 1px solid var(--border-primary);
  color: var(--text-secondary);
}

.cancel-btn:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

.submit-btn {
  background: #25D366;
  border: none;
  color: white;
  display: flex;
  align-items: center;
  gap: 8px;
}

.submit-btn:hover:not(:disabled) {
  background: #20c05a;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(37, 211, 102, 0.3);
}

.submit-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  background: #666;
}

.loading-spinner-small {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

/* Form Validation Hints */
.form-validation-hints {
  margin-top: 20px;
  padding: 16px;
  background: rgba(239, 68, 68, 0.05);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 8px;
}

.validation-hint-title {
  font-size: 14px;
  font-weight: 600;
  color: #EF4444;
  margin-bottom: 12px;
}

.validation-hints-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.validation-hints-list li {
  font-size: 13px;
  color: var(--text-secondary);
  padding: 6px 0;
  padding-left: 20px;
  position: relative;
}

.validation-hints-list li::before {
  content: '•';
  position: absolute;
  left: 8px;
  color: #EF4444;
  font-weight: bold;
}

/* Notification */
.notification-toast {
  position: fixed;
  bottom: 24px;
  right: 24px;
  min-width: 320px;
  max-width: 480px;
  padding: 16px 20px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  z-index: 2000;
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.notification-toast.success {
  border-left: 4px solid #22C55E;
}

.notification-toast.error {
  border-left: 4px solid #EF4444;
}

.notification-content {
  display: flex;
  gap: 12px;
  flex: 1;
}

.notification-content svg {
  flex-shrink: 0;
  margin-top: 2px;
}

.notification-toast.success svg {
  color: #22C55E;
}

.notification-toast.error svg {
  color: #EF4444;
}

.notification-text strong {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 4px;
}

.notification-text p {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0;
}

.notification-close {
  background: var(--bg-tertiary);
  border: none;
  border-radius: 4px;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.notification-close:hover {
  background: var(--bg-primary);
  color: var(--text-primary);
}

/* Dynamic Personalization Toggle */
.personalization-toggle {
  background: linear-gradient(135deg, rgba(37, 211, 102, 0.05) 0%, rgba(37, 211, 102, 0.02) 100%);
  border: 1px solid rgba(37, 211, 102, 0.2);
  border-radius: 12px;
  padding: 16px;
}

.toggle-label {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  margin: 0;
}

.toggle-checkbox {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-switch {
  position: relative;
  width: 48px;
  height: 26px;
  background: var(--border-primary);
  border-radius: 13px;
  transition: background 0.3s ease;
  flex-shrink: 0;
}

.toggle-switch::after {
  content: '';
  position: absolute;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: white;
  top: 3px;
  left: 3px;
  transition: transform 0.3s ease;
}

.toggle-checkbox:checked + .toggle-switch {
  background: #25D366;
}

.toggle-checkbox:checked + .toggle-switch::after {
  transform: translateX(22px);
}

.toggle-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.toggle-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}

.toggle-description {
  font-size: 12px;
  color: var(--text-secondary);
}

/* Variable Mapping Section */
.variable-mapping-section {
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  padding: 20px;
  margin-top: -8px;
}

.mapping-header h4 {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 4px 0;
}

.mapping-header p {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0 0 20px 0;
}

/* Split View Layout */
.mapping-split-view {
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: 20px;
  min-height: 500px;
}

.mappings-panel {
  display: flex;
  flex-direction: column;
}

.mappings-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.variable-mapping-row {
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  padding: 16px;
  display: grid;
  grid-template-columns: auto auto 1fr;
  gap: 16px;
  align-items: center;
  transition: all 0.2s ease;
}

.variable-mapping-row:hover {
  border-color: rgba(37, 211, 102, 0.3);
  box-shadow: 0 2px 8px rgba(37, 211, 102, 0.1);
}

.variable-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: center;
  min-width: 80px;
}

.variable-badge-large {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  background: rgba(37, 211, 102, 0.1);
  color: #25D366;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  font-family: 'Courier New', monospace;
  border: 2px solid rgba(37, 211, 102, 0.3);
}

.variable-description {
  font-size: 11px;
  color: var(--text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.mapping-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-tertiary);
}

.mapping-controls {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  flex: 1;
}

.control-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.control-group label {
  font-size: 12px;
  font-weight: 500;
  color: var(--text-secondary);
}

.field-select,
.default-input {
  padding: 10px 12px;
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  background: var(--bg-primary);
  color: var(--text-primary);
  font-size: 13px;
  transition: all 0.2s ease;
}

.field-select {
  cursor: pointer;
}

.field-select:hover {
  border-color: var(--border-secondary);
}

.field-select:focus,
.default-input:focus {
  outline: none;
  border-color: #25D366;
  box-shadow: 0 0 0 3px rgba(37, 211, 102, 0.1);
}

.default-input::placeholder {
  color: var(--text-tertiary);
  font-style: italic;
}

.mapping-example {
  grid-column: 1 / -1;
  margin-top: 8px;
  padding: 10px;
  background: rgba(59, 130, 246, 0.05);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 6px;
  font-size: 12px;
  display: flex;
  gap: 6px;
}

.example-label {
  font-weight: 600;
  color: #3B82F6;
}

.example-value {
  color: var(--text-primary);
  font-weight: 500;
}

.template-mapping-panel,
.preview-panel {
  display: flex;
  flex-direction: column;
}

.panel-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 2px solid var(--border-primary);
}

/* WhatsApp Template Display */
.whatsapp-template-display {
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 20px;
}

.template-component {
  margin-bottom: 16px;
}

.template-component:last-child {
  margin-bottom: 0;
}

.component-section {
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  overflow: hidden;
}

.component-label {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: var(--bg-tertiary);
  border-bottom: 1px solid var(--border-primary);
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.header-section .component-label {
  background: rgba(59, 130, 246, 0.05);
  color: #3B82F6;
}

.body-section .component-label {
  background: rgba(37, 211, 102, 0.05);
  color: #25D366;
}

.footer-section .component-label {
  background: rgba(107, 114, 128, 0.05);
  color: #6B7280;
}

.buttons-section .component-label {
  background: rgba(168, 85, 247, 0.05);
  color: #A855F7;
}

.component-content {
  padding: 14px;
}

.template-text {
  font-size: 14px;
  line-height: 1.6;
  color: var(--text-primary);
  margin-bottom: 12px;
  white-space: pre-wrap;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.footer-text {
  font-size: 12px;
  color: var(--text-secondary);
  margin-bottom: 0;
}

.media-header {
  text-align: center;
  padding: 20px;
}

.media-indicator {
  display: inline-block;
  padding: 8px 16px;
  background: var(--bg-tertiary);
  border-radius: 6px;
  font-size: 13px;
  color: var(--text-secondary);
}

.button-display {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  margin-bottom: 8px;
  font-size: 13px;
}

.button-display:last-child {
  margin-bottom: 0;
}

.button-icon {
  font-size: 16px;
}

.button-text {
  font-weight: 500;
  color: var(--text-primary);
}

.button-type {
  margin-left: auto;
  font-size: 11px;
  color: var(--text-tertiary);
  text-transform: uppercase;
}

/* Variable Mappings Inline */
.variable-mappings-inline {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.variable-mapper {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: rgba(37, 211, 102, 0.03);
  border: 1px solid rgba(37, 211, 102, 0.2);
  border-radius: 8px;
  transition: all 0.2s ease;
}

.variable-mapper:hover {
  background: rgba(37, 211, 102, 0.05);
  border-color: rgba(37, 211, 102, 0.3);
}

.variable-name {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  background: rgba(37, 211, 102, 0.1);
  color: #25D366;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  font-family: 'Courier New', monospace;
  min-width: 60px;
  justify-content: center;
}

.arrow-icon {
  flex-shrink: 0;
  color: var(--text-tertiary);
}

.inline-select {
  flex: 1;
  min-width: 180px;
  padding: 8px 12px;
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.inline-select:hover {
  border-color: var(--border-secondary);
}

.inline-select:focus {
  outline: none;
  border-color: #25D366;
  box-shadow: 0 0 0 3px rgba(37, 211, 102, 0.1);
}

.inline-default-input {
  flex: 0.8;
  min-width: 120px;
  padding: 8px 12px;
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  font-size: 13px;
  transition: all 0.2s ease;
}

.inline-default-input::placeholder {
  color: var(--text-tertiary);
  font-style: italic;
}

.inline-default-input:focus {
  outline: none;
  border-color: #25D366;
  box-shadow: 0 0 0 3px rgba(37, 211, 102, 0.1);
}

/* WhatsApp Phone Preview */
.preview-panel {
  position: sticky;
  top: 100px;
  align-self: flex-start;
}

.preview-phone {
  background: #E5DDD5;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  border: 1px solid var(--border-primary);
}

.phone-header {
  background: #075E54;
  color: white;
  padding: 12px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.phone-time {
  font-size: 12px;
  font-weight: 500;
}

.phone-title {
  font-size: 14px;
  font-weight: 600;
}

.phone-chat {
  padding: 16px;
  min-height: 400px;
  max-height: 600px;
  overflow-y: auto;
  background-image:
    repeating-linear-gradient(
      45deg,
      transparent,
      transparent 10px,
      rgba(0, 0, 0, 0.01) 10px,
      rgba(0, 0, 0, 0.01) 20px
    );
}

.chat-date {
  text-align: center;
  font-size: 12px;
  color: #667781;
  background: rgba(255, 255, 255, 0.9);
  padding: 4px 12px;
  border-radius: 12px;
  display: inline-block;
  margin: 0 auto 16px;
  width: fit-content;
  display: block;
}

.message-bubble {
  background: white;
  border-radius: 8px;
  padding: 0;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  max-width: 100%;
  position: relative;
  margin-left: 0;
  border-top-left-radius: 0;
  overflow: hidden;
}

.message-header {
  margin-bottom: 0;
}

.header-text {
  font-size: 15px;
  font-weight: 600;
  color: #000;
  margin-bottom: 4px;
  padding: 8px 12px 4px 12px;
}

.header-media {
  background: #F0F0F0;
  padding: 0;
  text-align: center;
  font-size: 13px;
  color: #667781;
  margin-bottom: 0;
}

.message-body {
  font-size: 14px;
  line-height: 1.5;
  color: #000;
  white-space: pre-wrap;
  word-wrap: break-word;
  padding: 8px 12px;
  margin-bottom: 0;
}

.message-footer {
  font-size: 12px;
  color: #667781;
  padding: 0 12px 8px 12px;
  margin-top: 0;
  border-top: 1px solid #E9EDEF;
}

.message-buttons {
  margin: 8px 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.wa-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px;
  background: white;
  border: 1px solid #D1D7DB;
  border-radius: 6px;
  color: #00A5F4;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.wa-button:hover {
  background: #F7F8FA;
}

.message-time {
  font-size: 11px;
  color: #667781;
  padding: 0 12px 8px 12px;
  text-align: right;
  margin-top: 4px;
}

.preview-info {
  background: rgba(37, 211, 102, 0.1);
  border-top: 1px solid rgba(37, 211, 102, 0.2);
  padding: 12px 16px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.info-icon {
  font-size: 16px;
  flex-shrink: 0;
}

.info-text {
  font-size: 12px;
  line-height: 1.4;
  color: #075E54;
}

/* Live Preview Section */
.live-preview-section {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid var(--border-primary);
}

.preview-header h5 {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 4px 0;
}

.preview-header p {
  font-size: 12px;
  color: var(--text-secondary);
  margin: 0 0 16px 0;
}

.preview-examples {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.preview-example {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.preview-customer-info {
  font-size: 12px;
  color: var(--text-secondary);
}

.whatsapp-bubble {
  background: #DCF8C6;
  color: #000;
  padding: 12px 14px;
  border-radius: 8px;
  border-top-left-radius: 0;
  font-size: 14px;
  line-height: 1.5;
  max-width: 100%;
  word-wrap: break-word;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* Two-Column Modal Layout */
.modal-two-column-layout {
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 24px;
  min-height: 500px;
}

.left-form-column {
  overflow-y: auto;
  max-height: calc(90vh - 120px);
  padding-right: 12px;
}

.right-preview-column {
  display: flex;
  flex-direction: column;
}

.sticky-preview-wrapper {
  position: sticky;
  top: 0;
  align-self: flex-start;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* Read-only Info Field */
.info-field {
  padding: 12px 16px;
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  font-size: 14px;
  color: var(--text-secondary);
  font-style: italic;
}

/* Variable Mappings List */
.variable-mappings-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Variable Mapping Card */
.variable-mapping-card {
  padding: 16px;
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  transition: all 0.2s ease;
}

.variable-mapping-card:hover {
  border-color: rgba(37, 211, 102, 0.3);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.mapping-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  gap: 12px;
}

.mapping-type-toggle {
  display: flex;
  background: var(--bg-secondary);
  border-radius: 6px;
  padding: 2px;
  gap: 2px;
}

.toggle-option {
  display: flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 12px;
  font-weight: 500;
  color: var(--text-secondary);
}

.toggle-option input[type="radio"] {
  display: none;
}

.toggle-option.active {
  background: #25D366;
  color: white;
}

.mapping-input-row {
  display: flex;
  gap: 10px;
  align-items: center;
}

.mapping-select {
  flex: 1;
  min-width: 180px;
  padding: 8px 12px;
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.mapping-select:hover {
  border-color: var(--border-secondary);
}

.mapping-select:focus {
  outline: none;
  border-color: #25D366;
  box-shadow: 0 0 0 3px rgba(37, 211, 102, 0.1);
}

.mapping-input {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  font-size: 13px;
  transition: all 0.2s ease;
}

.mapping-input.full-width {
  width: 100%;
}

.mapping-input::placeholder {
  color: var(--text-tertiary);
  font-style: italic;
}

.mapping-input:focus {
  outline: none;
  border-color: #25D366;
  box-shadow: 0 0 0 3px rgba(37, 211, 102, 0.1);
}

.compact-variable-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  transition: all 0.2s ease;
}

.compact-variable-row:hover {
  border-color: rgba(37, 211, 102, 0.3);
  background: rgba(37, 211, 102, 0.02);
}

.variable-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  background: rgba(37, 211, 102, 0.1);
  color: #25D366;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 700;
  font-family: 'Courier New', monospace;
  min-width: 60px;
  border: 2px solid rgba(37, 211, 102, 0.3);
}

.compact-variable-row .arrow-icon {
  flex-shrink: 0;
  color: var(--text-tertiary);
}

.compact-field-select {
  flex: 1;
  min-width: 180px;
  padding: 8px 12px;
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.compact-field-select:hover {
  border-color: var(--border-secondary);
}

.compact-field-select:focus {
  outline: none;
  border-color: #25D366;
  box-shadow: 0 0 0 3px rgba(37, 211, 102, 0.1);
}

.compact-default-input {
  flex: 0.8;
  min-width: 120px;
  padding: 8px 12px;
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  font-size: 13px;
  transition: all 0.2s ease;
}

.compact-default-input::placeholder {
  color: var(--text-tertiary);
  font-style: italic;
}

.compact-default-input:focus {
  outline: none;
  border-color: #25D366;
  box-shadow: 0 0 0 3px rgba(37, 211, 102, 0.1);
}

/* Recipients Info Card */
.recipients-info-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(139, 92, 246, 0.08) 100%);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 12px;
}

.recipients-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: rgba(99, 102, 241, 0.12);
  border-radius: 12px;
  color: #6366F1;
  flex-shrink: 0;
}

.recipients-content {
  flex: 1;
}

.recipients-label {
  font-size: 13px;
  font-weight: 500;
  color: var(--text-secondary);
  margin-bottom: 4px;
}

.recipients-count {
  font-size: 28px;
  font-weight: 700;
  color: #6366F1;
  line-height: 1;
  margin-bottom: 4px;
}

.recipients-hint {
  font-size: 12px;
  color: var(--text-tertiary);
  font-style: italic;
}

/* Phone Numbers List */
.phone-numbers-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.phone-number-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.phone-input {
  flex: 1;
  padding: 10px 14px;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  background: var(--bg-primary);
  color: var(--text-primary);
  font-size: 14px;
  font-family: 'Courier New', monospace;
  transition: all 0.2s ease;
}

.phone-input:focus {
  outline: none;
  border-color: #25D366;
  box-shadow: 0 0 0 3px rgba(37, 211, 102, 0.1);
}

/* Customer Search Styles */
.customer-search-container {
  position: relative;
  margin-bottom: 20px;
}

.search-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 14px;
  color: var(--text-secondary);
  pointer-events: none;
}

.customer-search-input {
  width: 100%;
  padding: 12px 14px 12px 44px;
  border: 2px solid var(--border-primary);
  border-radius: 8px;
  background: var(--bg-tertiary);
  color: var(--text-primary);
  font-size: 14px;
  transition: all 0.2s ease;
}

.customer-search-input:focus {
  outline: none;
  border-color: #25D366;
  box-shadow: 0 0 0 3px rgba(37, 211, 102, 0.1);
}

.search-loading-spinner {
  position: absolute;
  right: 14px;
  width: 20px;
  height: 20px;
  border: 2px solid var(--border-primary);
  border-top-color: #25D366;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.customer-search-results {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 8px;
  max-height: 300px;
  overflow-y: auto;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
}

.customer-result-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.2s ease;
  border-bottom: 1px solid var(--border-primary);
}

.customer-result-item:last-child {
  border-bottom: none;
}

.customer-result-item:hover {
  background: rgba(37, 211, 102, 0.05);
}

.customer-result-info {
  flex: 1;
}

.customer-result-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 4px;
}

.customer-result-details {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: var(--text-secondary);
}

.customer-result-phone {
  font-family: monospace;
}

.customer-result-category {
  padding: 2px 8px;
  background: rgba(37, 211, 102, 0.1);
  border-radius: 4px;
  color: #25D366;
  font-weight: 500;
}

.add-customer-icon {
  color: #25D366;
  flex-shrink: 0;
}

.no-results {
  padding: 20px;
  text-align: center;
  color: var(--text-secondary);
  font-size: 14px;
}

/* Selected Customers Pills */
.selected-customers-container {
  margin-top: 20px;
}

.selected-customers-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 12px;
}

.selected-customers-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.customer-pill {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  transition: all 0.2s ease;
}

.customer-pill:hover {
  border-color: #25D366;
  box-shadow: 0 2px 8px rgba(37, 211, 102, 0.1);
}

.customer-pill-content {
  flex: 1;
}

.customer-pill-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 3px;
}

.customer-pill-details {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: var(--text-secondary);
}

.customer-pill-phone {
  font-family: monospace;
}

.customer-pill-category {
  color: #25D366;
  font-weight: 500;
}

.customer-pill-remove {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  background: rgba(239, 68, 68, 0.1);
  color: #EF4444;
  border-radius: 50%;
  cursor: pointer;
  font-size: 18px;
  font-weight: bold;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.customer-pill-remove:hover {
  background: #EF4444;
  color: white;
}

/* Empty State */
.empty-customer-list {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  background: var(--bg-tertiary);
  border: 2px dashed var(--border-primary);
  border-radius: 12px;
  color: var(--text-secondary);
}

.empty-customer-list p {
  margin-top: 12px;
  font-size: 14px;
}

.remove-phone-btn {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  color: #EF4444;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.remove-phone-btn:hover {
  background: rgba(239, 68, 68, 0.1);
  border-color: #EF4444;
}

.add-phone-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  background: transparent;
  border: 1px dashed var(--border-primary);
  border-radius: 8px;
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
}

.add-phone-btn:hover {
  background: rgba(37, 211, 102, 0.05);
  border-color: #25D366;
  color: #25D366;
  border-style: solid;
}

/* Category Count Display */
.category-name-with-count {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 1;
  gap: 12px;
}

.category-name {
  font-size: 14px;
  color: var(--text-primary);
  font-weight: 500;
}

.category-count {
  font-size: 12px;
  color: var(--text-secondary);
  background: var(--bg-tertiary);
  padding: 4px 10px;
  border-radius: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.checkbox-label:hover .category-count {
  background: rgba(37, 211, 102, 0.1);
  color: #25D366;
}
.tag-name-with-count {
  display: flex;
  align-items: center;
  flex: 1;
  gap: 8px;
}
.tag-color-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}
.tag-name {
  font-size: 14px;
  color: var(--text-primary);
  font-weight: 500;
  flex: 1;
}

/* Responsive */
@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
  }

  .modal-two-column-layout {
    grid-template-columns: 1fr;
  }

  .right-preview-column {
    order: -1;
  }

  .sticky-preview-wrapper {
    position: relative;
  }

  .left-form-column {
    max-height: none;
    padding-right: 0;
  }

  .compact-variable-row {
    flex-direction: column;
    align-items: stretch;
  }

  .compact-field-select,
  .compact-default-input {
    min-width: 100%;
  }

  .page-content {
    padding: 16px;
  }

  .page-header {
    flex-direction: column;
    gap: 16px;
  }

  .create-campaign-btn {
    width: 100%;
    justify-content: center;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .campaigns-table-wrapper {
    padding: 0 8px 16px;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .notification-toast {
    left: 16px;
    right: 16px;
    min-width: auto;
  }

  .mapping-row {
    grid-template-columns: 1fr;
  }

  .variable-mapping-section {
    padding: 16px;
  }

  .mapping-split-view {
    grid-template-columns: 1fr;
  }

  .preview-panel {
    position: relative;
    top: 0;
  }

  .phone-chat {
    max-height: 400px;
  }
}

/* Media Preview Styles */
.message-header .header-media {
  margin: 0;
  border-radius: 0;
  overflow: hidden;
  border-top-right-radius: 8px;
}

.preview-image {
  width: 100%;
  height: 180px;
  object-fit: cover;
  display: block;
  background: #f0f2f5;
}

.preview-video {
  width: 100%;
  height: 180px;
  display: block;
  background: #000;
  object-fit: contain;
}

.preview-document {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  background: #f0f2f5;
  color: #111b21;
  text-decoration: none;
  transition: background 0.2s;
  margin: 0;
}

.preview-document:hover {
  background: #e9edef;
}

.preview-document svg {
  flex-shrink: 0;
  color: #00a884;
  width: 20px;
  height: 20px;
}

.preview-document span {
  font-size: 13px;
  font-weight: 400;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #111b21;
}

.media-placeholder {
  height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
  background: linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%);
  color: #667781;
  font-size: 48px;
  position: relative;
}

.media-placeholder::after {
  content: 'Enter URL to preview';
  font-size: 12px;
  color: #8696a0;
  position: absolute;
  bottom: 16px;
}

.media-error {
  background: linear-gradient(135deg, #ffebee 0%, #fce4ec 100%);
}

.media-error::after {
  content: 'Invalid or broken URL';
  color: #ef4444;
}

/* Excel Upload Styles */
.excel-phone-warning {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 10px;
  padding: 10px 12px;
  background: #fef9ec;
  border: 1px solid #e67e22;
  border-radius: 6px;
  color: #7d4e00;
  font-size: 13px;
  line-height: 1.5;
}

.excel-phone-warning svg {
  flex-shrink: 0;
  margin-top: 2px;
}

.excel-upload-container {
  margin-bottom: 20px;
}

.upload-excel-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #25D366;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
}

.upload-excel-btn:hover {
  background: #20c05a;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(37, 211, 102, 0.3);
}

.upload-excel-btn:disabled {
  background: var(--bg-tertiary);
  color: var(--text-secondary);
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.file-uploaded-indicator {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: var(--bg-tertiary);
  border: 2px solid #25D366;
  border-radius: 8px;
}

.file-name {
  font-weight: 500;
  color: var(--text-primary);
}

.file-rows {
  color: var(--text-secondary);
  font-size: 13px;
}

.clear-file-btn {
  margin-left: auto;
  background: none;
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: var(--text-secondary);
  transition: color 0.2s;
  line-height: 1;
}

.clear-file-btn:hover {
  color: #e74c3c;
}

/* Excel Phone Column Highlight Section */
.excel-phone-column-highlight {
  margin: 20px 0;
  padding: 20px;
  background: rgba(37, 211, 102, 0.05);
  border: 2px solid #25D366;
  border-radius: 12px;
}

.highlight-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: rgba(37, 211, 102, 0.1);
  border-radius: 8px;
  margin-bottom: 16px;
}

.highlight-banner svg {
  flex-shrink: 0;
}

.highlight-banner span {
  font-weight: 600;
  font-size: 14px;
  color: #25D366;
}

.excel-phone-column-highlight label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: var(--text-primary);
}

.excel-column-select {
  width: 100%;
  padding: 14px;
  border: 2px solid #25D366;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  background: var(--bg-tertiary);
  color: var(--text-primary);
  transition: all 0.2s ease;
}

.excel-column-select:focus {
  outline: none;
  border-color: #20c05a;
  box-shadow: 0 0 0 3px rgba(37, 211, 102, 0.1);
}

.excel-summary {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding: 10px 12px;
  background: var(--bg-secondary);
  border-radius: 6px;
  color: var(--text-secondary);
  font-size: 13px;
}

/* Variable Mapping Three-Way Selector */
.mapping-type-selector {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.type-option {
  flex: 1;
  min-width: 120px;
  padding: 10px 12px;
  border: 2px solid var(--border-primary);
  border-radius: 8px;
  cursor: pointer;
  text-align: center;
  transition: all 0.2s ease;
  background: var(--bg-tertiary);
  color: var(--text-secondary);
  font-size: 12px;
  white-space: nowrap;
}

.type-option.active {
  border-color: #25D366;
  background: rgba(37, 211, 102, 0.1);
  color: #25D366;
  font-weight: 600;
}

.type-option input[type="radio"] {
  display: none;
}

.type-option:hover {
  border-color: #25D366;
  background: var(--bg-secondary);
}

.mapping-input-container {
  margin-top: 12px;
}

.full-width {
  width: 100%;
}

/* Daily Messaging Limit Card */
.daily-limit-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  border-radius: 10px;
  border: 1px solid var(--border-primary);
  background: var(--bg-primary);
  margin-bottom: 20px;
  width: fit-content;
}

.daily-limit-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
  flex-shrink: 0;
}

.daily-limit-body {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.daily-limit-label {
  font-size: 12px;
  color: var(--text-secondary);
  font-weight: 500;
}

.daily-limit-value {
  font-size: 15px;
  font-weight: 700;
  color: var(--text-primary);
}

/* WhatsApp Access Gate */
.wa-gate {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 80px 24px;
  gap: 16px;
}

.wa-gate-icon {
  color: #6b7280;
  margin-bottom: 8px;
}

.wa-gate-icon--warning {
  color: #f59e0b;
}

.wa-gate-title {
  font-size: 22px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.wa-gate-message {
  font-size: 15px;
  color: var(--text-secondary);
  margin: 0;
  max-width: 420px;
  line-height: 1.6;
}

.wa-gate-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 28px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  margin-top: 8px;
  transition: all 0.2s;
}

.wa-gate-btn--primary {
  background: #3b82f6;
  color: #fff;
}

.wa-gate-btn--primary:hover {
  background: #2563eb;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

/* ── Media Picker (shared with templates view) ───────────────────────────── */
.media-picker-tabs {
  display: flex;
  gap: 4px;
  background: var(--bg-tertiary);
  border-radius: 8px;
  padding: 4px;
  margin-bottom: 10px;
}
.media-tab {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 7px 10px;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  color: var(--text-secondary);
  background: transparent;
  cursor: pointer;
  transition: all 0.15s;
}
.media-tab:hover { color: var(--text-primary); }
.media-tab--active {
  background: var(--bg-card);
  color: var(--text-primary);
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}
.media-picker-panel { min-height: 60px; }
.media-upload-area {
  border: 2px dashed var(--border-secondary);
  border-radius: 8px;
  padding: 20px 16px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}
.media-upload-area:hover { border-color: #10b981; background: rgba(16, 185, 129, 0.08); }
.upload-icon { display: block; color: var(--text-tertiary); margin: 0 auto 6px; }
.upload-area-text { font-size: 13px; color: var(--text-primary); margin: 0 0 3px; }
.upload-area-hint { font-size: 12px; color: var(--text-secondary); margin: 0; }
.media-file-hidden { display: none; }
.media-uploading {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 0;
  font-size: 13px;
  color: var(--text-secondary);
}
.media-asset-confirmed {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: rgba(16, 185, 129, 0.08);
  border: 1px solid rgba(16, 185, 129, 0.3);
  border-radius: 8px;
}
.media-asset-name {
  flex: 1;
  font-size: 13px;
  font-weight: 500;
  color: var(--text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.media-clear-btn {
  background: none;
  border: 1px solid var(--border-secondary);
  border-radius: 6px;
  padding: 3px 10px;
  font-size: 12px;
  color: var(--text-secondary);
  cursor: pointer;
  flex-shrink: 0;
}
.media-clear-btn:hover { background: var(--bg-card); color: var(--text-primary); }
.media-upload-error {
  margin-top: 8px;
  font-size: 12px;
  color: var(--error-color);
  padding: 7px 12px;
  background: var(--error-bg-light);
  border-radius: 6px;
}
.media-loading-state,
.media-empty-state {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 18px;
  color: var(--text-secondary);
  font-size: 13px;
}
.media-assets-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(90px, 1fr));
  gap: 8px;
  max-height: 260px;
  overflow-y: auto;
  padding: 2px;
}
.media-asset-card {
  position: relative;
  border: 2px solid var(--border-primary);
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.15s;
  overflow: hidden;
  aspect-ratio: 1;
  background: var(--bg-tertiary);
}
.media-asset-card:hover { border-color: #10b981; }
.media-asset-card--selected { border-color: #10b981; }
.asset-preview-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.asset-preview-fallback {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 10px;
  color: var(--text-secondary);
}
.asset-fallback-name {
  font-size: 10px;
  color: var(--text-tertiary);
  text-align: center;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  word-break: break-all;
}
.asset-fallback-size { font-size: 10px; color: var(--text-tertiary); }
.asset-name-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 10px;
  padding: 4px 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
}
.asset-selected-badge {
  position: absolute;
  top: 5px;
  right: 5px;
  width: 20px;
  height: 20px;
  background: #10b981;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}
.url-input-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
  border: 2px solid var(--border-secondary);
  border-radius: 10px;
  padding: 10px 14px;
  background: var(--bg-primary);
  transition: border-color 0.2s, box-shadow 0.2s;
}
.url-input-wrap:focus-within {
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.12);
}
.url-input-wrap.url-input--valid {
  border-color: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
}
.url-input-wrap.url-input--invalid {
  border-color: #ef4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}
.url-prefix-icon { color: var(--text-tertiary); flex-shrink: 0; }
.url-text-input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 13px;
  color: var(--text-primary);
  min-width: 0;
}
.url-text-input::placeholder { color: var(--text-tertiary); }
.url-status-icon { flex-shrink: 0; }
.url-valid-icon { color: #10b981; }
.url-invalid-icon { color: #ef4444; }
.url-ext-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  flex-wrap: wrap;
}
.url-ext-label { font-size: 11px; color: var(--text-tertiary); }
.url-ext-chip {
  font-size: 11px;
  font-family: monospace;
  padding: 2px 7px;
  border-radius: 4px;
  border: 1px solid var(--border-secondary);
  color: var(--text-secondary);
  background: var(--bg-secondary);
  transition: all 0.15s;
}
.url-ext-chip--match {
  border-color: #10b981;
  color: #10b981;
  background: rgba(16, 185, 129, 0.08);
}
.url-error-msg {
  margin-top: 7px;
  font-size: 12px;
  color: var(--error-color);
}

/* ─── WhatsApp Budget Card (Modern) ────────────────────────────────────── */
.wa-budget-card {
  position: relative;
  background: var(--bg-card);
  border: 1px solid var(--border-primary);
  border-radius: 16px;
  margin-bottom: 16px;
  overflow: hidden;
}
.wa-budget-card::before {
  content: '';
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 2px;
  background: linear-gradient(90deg, #25D366 0%, #128C7E 60%, #075E54 100%);
  z-index: 1;
}
.wa-budget-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px 12px;
}
.wa-budget-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
}
.wa-budget-icon-badge {
  width: 26px;
  height: 26px;
  border-radius: 8px;
  background: rgba(37,211,102,0.15);
  color: #25D366;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.wa-budget-header-right {
  display: flex;
  align-items: center;
  gap: 8px;
}
.wa-budget-market-chip {
  font-size: 11px;
  padding: 3px 10px;
  border-radius: 20px;
  background: rgba(37,211,102,0.1);
  color: #25D366;
  font-weight: 600;
  border: 1px solid rgba(37,211,102,0.2);
}
.budget-edit-btn {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 600;
  padding: 5px 11px;
  border-radius: 8px;
  border: 1px solid var(--border-primary);
  background: var(--bg-secondary);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.15s;
}
.budget-edit-btn:hover {
  border-color: #25D366;
  color: #25D366;
  background: rgba(37,211,102,0.06);
}
.wa-budget-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: var(--text-secondary);
  padding: 8px 20px 20px;
}
.wa-budget-body {
  display: flex;
  flex-direction: column;
  padding: 0 20px 20px;
}
/* nudge banner */
.budget-nudge-banner {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: rgba(245,158,11,0.08);
  border: 1px solid rgba(245,158,11,0.25);
  border-radius: 10px;
  padding: 10px 13px;
  font-size: 12.5px;
  color: #f59e0b;
  line-height: 1.5;
  margin-bottom: 16px;
}
.budget-nudge-banner svg { flex-shrink: 0; margin-top: 1px; }
/* hero row */
.wa-budget-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}
.wa-budget-hero-left {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}
.wa-budget-hero-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: var(--text-tertiary);
}
.wa-budget-hero-amount {
  font-size: 28px;
  font-weight: 800;
  color: var(--text-primary);
  letter-spacing: -0.5px;
  line-height: 1.1;
}
.wa-budget-hero-sub {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: var(--text-secondary);
  margin-top: 2px;
  flex-wrap: wrap;
}
.wa-budget-hero-pct {
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 20px;
}
.wa-budget-hero-pct--good   { background: rgba(37,211,102,0.12); color: #25D366; }
.wa-budget-hero-pct--warn   { background: rgba(245,158,11,0.12); color: #f59e0b; }
.wa-budget-hero-pct--danger { background: rgba(239,68,68,0.12);  color: #ef4444; }
.wa-budget-hero-sep { color: var(--text-tertiary); }
/* ring chart */
.wa-budget-ring-wrap {
  position: relative;
  width: 80px;
  height: 80px;
  flex-shrink: 0;
  margin-left: 12px;
}
.wa-budget-ring-center {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.wa-budget-ring-pct {
  font-size: 12px;
  font-weight: 800;
  color: var(--text-primary);
}
/* metric tiles */
.wa-budget-tiles {
  display: flex;
  align-items: stretch;
  background: var(--bg-secondary);
  border: 1px solid var(--border-secondary);
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 14px;
}
.wa-budget-tile {
  flex: 1;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}
.wa-budget-tile-sep {
  width: 1px;
  background: var(--border-secondary);
  flex-shrink: 0;
}
.wa-budget-tile-top {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 1px;
}
.wa-budget-tile-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-tertiary);
}
.wa-budget-tile-value {
  font-size: 15px;
  font-weight: 700;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.wa-budget-tile-sub {
  font-size: 10px;
  color: var(--text-tertiary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.4;
}
.wa-budget-diff {
  font-size: 10px;
  font-weight: 700;
  padding: 1px 5px;
  border-radius: 20px;
}
.wa-budget-diff--up   { background: rgba(239,68,68,0.1);  color: #ef4444; }
.wa-budget-diff--down { background: rgba(16,185,129,0.1); color: #10b981; }
/* spend breakdown */
.wa-budget-breakdown {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 14px;
}
.wa-budget-breakdown-title {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-tertiary);
  margin-bottom: 2px;
}
.wa-budget-breakdown-row {
  display: flex;
  align-items: center;
  gap: 9px;
}
.wa-budget-breakdown-label {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 5px;
  width: 28px;
  text-align: center;
  flex-shrink: 0;
}
.wa-budget-breakdown-label--mkt  { background: rgba(99,102,241,0.12); color: #6366f1; }
.wa-budget-breakdown-label--util { background: rgba(16,185,129,0.12); color: #10b981; }
.wa-budget-breakdown-track {
  flex: 1;
  height: 6px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-secondary);
  border-radius: 99px;
  overflow: hidden;
}
.wa-budget-breakdown-bar {
  height: 100%;
  border-radius: 99px;
  transition: width 0.5s ease;
}
.wa-budget-breakdown-bar--mkt  { background: linear-gradient(90deg, #818cf8, #6366f1); }
.wa-budget-breakdown-bar--util { background: linear-gradient(90deg, #34d399, #10b981); }
.wa-budget-breakdown-val {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-primary);
  white-space: nowrap;
  min-width: 50px;
  text-align: right;
}
.wa-budget-breakdown-pct {
  font-size: 10px;
  color: var(--text-tertiary);
  width: 28px;
  text-align: right;
  flex-shrink: 0;
}
/* history chart */
.wa-budget-history {
  border-top: 1px solid var(--border-secondary);
  padding-top: 14px;
}
.wa-budget-history-title {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-tertiary);
  margin-bottom: 12px;
}
.wa-budget-history-bars {
  display: flex;
  align-items: flex-end;
  gap: 8px;
}
.wa-budget-history-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  flex: 1;
}
.wa-budget-history-bar-wrap {
  width: 100%;
  height: 72px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-secondary);
  border-radius: 8px;
  display: flex;
  align-items: flex-end;
  overflow: hidden;
}
.wa-budget-history-bar {
  width: 100%;
  background: var(--border-primary);
  border-radius: 6px 6px 0 0;
  transition: height 0.5s ease;
}
.wa-budget-history-bar--current {
  background: linear-gradient(180deg, #34d399 0%, #25D366 100%);
  box-shadow: 0 -4px 14px rgba(37,211,102,0.35);
}
.wa-budget-history-amt {
  font-size: 11px;
  font-weight: 600;
  color: var(--text-primary);
  text-align: center;
}
.wa-budget-history-lbl {
  font-size: 10px;
  color: var(--text-tertiary);
}
/* empty state */
.wa-budget-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 28px 20px 24px;
  text-align: center;
}
.wa-budget-empty-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-tertiary);
  margin-bottom: 2px;
}
.wa-budget-empty-copy {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
  color: var(--text-secondary);
  line-height: 1.5;
}
.wa-budget-empty-copy strong { color: var(--text-primary); font-size: 14px; }
.wa-budget-empty-btn {
  margin-top: 4px;
  padding: 8px 20px;
  border-radius: 10px;
  border: none;
  background: #25D366;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s, transform 0.1s;
}
.wa-budget-empty-btn:hover  { background: #1ebe5d; transform: translateY(-1px); }
.wa-budget-empty-btn:active { transform: translateY(0); }
/* spend diff chip (compat) */
.spend-diff-chip {
  display: inline-flex;
  align-items: center;
  font-size: 10px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 20px;
  margin-left: 5px;
  vertical-align: middle;
}
.spend-diff-chip--up   { background: rgba(239,68,68,0.1);  color: #ef4444; }
.spend-diff-chip--down { background: rgba(16,185,129,0.1); color: #10b981; }

/* ─── Budget Settings Modal ─────────────────────────────────────────────── */
.budget-settings-modal { max-width: 500px; }
.budget-modal-body { padding: 20px 24px 24px; display: flex; flex-direction: column; gap: 18px; }
.budget-input-wrap {
  display: flex;
  align-items: center;
  border: 1.5px solid var(--border-primary);
  border-radius: 10px;
  overflow: hidden;
  background: var(--bg-primary);
  transition: border-color 0.2s;
}
.budget-input-wrap:focus-within { border-color: #25D366; }
.budget-input-prefix {
  padding: 0 10px 0 14px;
  font-size: 15px;
  font-weight: 600;
  color: var(--text-secondary);
  background: var(--bg-secondary);
  border-right: 1px solid var(--border-secondary);
  height: 40px;
  display: flex;
  align-items: center;
}
.budget-amount-input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  padding: 10px 12px;
  font-size: 14px;
  color: var(--text-primary);
}
.budget-input-suffix {
  padding: 0 14px;
  font-size: 12px;
  color: var(--text-tertiary);
  white-space: nowrap;
  background: var(--bg-secondary);
  border-left: 1px solid var(--border-secondary);
  height: 40px;
  display: flex;
  align-items: center;
}

/* Pricing reference box */
.pricing-reference-box {
  background: var(--bg-secondary);
  border: 1px solid var(--border-secondary);
  border-radius: 10px;
  padding: 14px 16px;
}
.pricing-ref-header {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 12px;
}
.pricing-ref-rows { display: flex; flex-direction: column; gap: 8px; }
.pricing-ref-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 13px;
}
.pricing-ref-price { color: var(--text-secondary); }
.pricing-ref-price em { font-style: normal; color: var(--text-tertiary); font-size: 11px; }
.pricing-ref-source {
  font-size: 11px;
  color: var(--text-tertiary);
  margin-top: 10px;
  margin-bottom: 0;
}
.pricing-type-chip {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 9px;
  border-radius: 20px;
}
.pricing-type-chip--marketing {
  background: rgba(99,102,241,0.1);
  color: #6366f1;
  border: 1px solid rgba(99,102,241,0.2);
}
.pricing-type-chip--utility {
  background: rgba(16,185,129,0.1);
  color: #10b981;
  border: 1px solid rgba(16,185,129,0.2);
}

/* ─── Campaign Cost Estimate (in confirmation dialog) ───────────────────── */
.campaign-cost-estimate {
  background: var(--bg-secondary);
  border: 1px solid var(--border-secondary);
  border-radius: 10px;
  padding: 14px 16px;
  margin: 12px 0;
}
.cost-estimate-header {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-tertiary);
  margin-bottom: 12px;
}
.cost-estimate-rows { display: flex; flex-direction: column; gap: 7px; }
.cost-estimate-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 13px;
  color: var(--text-secondary);
}
.cost-estimate-row span:first-child { color: var(--text-tertiary); }
.cost-estimate-row--total {
  font-weight: 700;
  color: var(--text-primary);
  padding-top: 8px;
  border-top: 1px solid var(--border-secondary);
  margin-top: 4px;
}
.cost-estimate-row--total span:first-child { color: var(--text-primary); }
.cost-estimate-row--over strong { animation: pulse-red 1.5s ease-in-out infinite; }
@keyframes pulse-red { 0%, 100% { opacity: 1; } 50% { opacity: 0.6; } }
.cost-estimate-divider {
  height: 1px;
  background: var(--border-secondary);
  margin: 4px 0;
}
.cost-type-badge {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 9px;
  border-radius: 20px;
}
.cost-type-badge--marketing {
  background: rgba(99,102,241,0.1);
  color: #6366f1;
}
.cost-type-badge--utility {
  background: rgba(16,185,129,0.1);
  color: #10b981;
}
.cost-no-budget-hint {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 12px;
  color: var(--text-tertiary);
  margin: 10px 0;
  line-height: 1.5;
}

/* ─── Campaigns table: cost columns ─────────────────────────────────────── */
.cost-cell { white-space: nowrap; }
.cost-value { font-size: 12px; font-weight: 600; color: var(--text-primary); }
.cost-per-delivered { color: #6366f1; }
.cost-na { color: var(--text-tertiary); font-size: 13px; }

/* Template category chip in table */
.template-cat-chip {
  font-size: 9px;
  font-weight: 700;
  padding: 1px 5px;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.template-cat-chip--mkt { background: rgba(99,102,241,0.12); color: #6366f1; }
.template-cat-chip--util { background: rgba(16,185,129,0.12); color: #10b981; }

/* ─── Over-budget confirm button ─────────────────────────────────────────── */
.confirm-dialog-confirm--blocked {
  background: rgba(239,68,68,0.1) !important;
  color: #ef4444 !important;
  cursor: not-allowed !important;
  border: 1px solid rgba(239,68,68,0.3) !important;
  display: flex;
  align-items: center;
  gap: 6px;
}
.confirm-dialog-confirm--blocked:hover { opacity: 1 !important; }
</style>
